import { htmlEncode } from '../utils/escape.utils';
import { SubscriptionType } from '../license/types/SubscriptionType';
import { MonthlyProductType } from '../license/types/MonthlyProductType';
import { NirvanaErrorCode } from '@nirvana/shared/dtos/types/shared/communication';
import {
  IntegrationResponseStatus,
  IntegrationStatus,
  MetadataStatus,
  SensorResponseStatus,
  SensorStatus,
} from '../xdr/common/types';
import { IntegrationType } from '../xdr/types/integration-type';
import { SensorType } from '../xdr/sensors/types/sensor-type';
import { NSVAVirtualApplianceErrorReasons, SensorConnectivityErrors } from '../xdr/sensors/constants';
import { MdrServiceFlavor } from '../license/types/MdrServiceFlavor';
import { ProductServiceStatus } from '../license/types/ProductServiceStatus';
import { SendFeedbackEmailConstants } from '../xdr/sendFeedbackEmail/SendFeedbackEmailConstants';
import { KILL_CHAIN_PHASES } from '../edr/incidentsGrid/types';
import { IMConstants } from '../integrityMonitor/common/utils/IMConstants';
import { IncidentType } from '../edr/incidentsSideMenu/types';
import { Status as XdrResponseStatus, XdrResponseErrorId } from '../xdr/responseGrid/types';
import { PackageNameMappings, PackageNameMappingsHasManageCompanies } from '../packages/packagesGrid/types';
import { ScanTypes } from '../packages/common/packageScanModesForm/types/ScanTypes';
import { translateFn } from '../utils/translator.helper';
import { convertUnits } from '../remoteShell/uploadWindow/utils';
import { AUTH_VALUES } from '../network/installAgent/common/credentialsManagerGrid/types';
import { TaskSelectionStatus } from '../tasks/networkTasks/types';
import { LicenseProductIdEnum, PRODUCT_ID_TO_NAME } from '@nirvana/shared/license-helper';
import {
  CLOUD_SECURITY_NUMBER_OF_ITEMS,
  ERA_NUMBER_OF_ITEMS,
  RisksSectionsStrings,
} from '../xdr/riskIncidentPanel/types';
import { NextScanInclusionStatus, ScanAction } from '../easm/assetsInventoryGrid/types';
import { EASMCountryType, DNSRecordType } from '../easm/artifactsInventoryGrid/types';
import {
  Action as ResponseActionsAction,
  ErrorId as ResponseActionsErrorId,
  Source as ResponseActionsSource,
  Status as ResponseActionsStatus,
  Target as ResponseActionsTarget,
  Type as ResponseActionsType,
} from '../edr/responseGrid/types';
import {
  AffectedDeviceType,
  CloudProviders,
  Compliance,
  FixRisksErrorCodes,
  MisconfigurationType,
  MitigationType,
  RiskTypes,
  RollbackRisksErrorCodes,
  ScoreTypes,
  State,
  Status,
  Watchlist,
} from '../riskManagement/common/constants';
import { OperatingSystemType } from '@nirvana/shared/dtos/types/protected-entities/common';
import { UserType } from '../riskManagement/usersApp/types';
import { ApplicationType, VulnerabilitiesSources } from '../riskManagement/vulnerabilitiesApp/types';
import { AnswerType } from '../applicationCveList/utils';
import { Type as CustomRulesAutomaticActionsType } from '../customRules/addRule/component/ruleAutomaticActions/types';
import { ControlStatus } from '@nirvana/shared/dtos/types/compliance-reports/gz-console';
import { DetectedIncidentsTemplatesStringKeys } from '@nirvana/shared/dtos/types/widgets/common';
import { ExpirationStatus, FindingType } from '../easm/types';
import { InvestigationStatus, AssignedUserType, InvestigationPriority } from '../investigation/types';
import { DomainControllerRoleEnum } from '../xdr/sensors/ad-onprem/types';

export class BaseWebComponentTranslator {
  private static instance: BaseWebComponentTranslator;
  protected dependenciesInjected: boolean;
  protected dependencies: any;

  private constructor() {
    this.initialSetup();
  }

  private translations: {
    subscriptionType?: {
      [type in SubscriptionType]: () => string; // lazy translate
    };
    licenseProductId?: {
      [productId in LicenseProductIdEnum]: () => string; // lazy translate
    };
    licenseProductType?: {
      [productType in MonthlyProductType]: () => string; // lazy translate
    };
    licenseUnitMapping?: {
      [productType in LicenseProductIdEnum]?: () => string; // lazy translate
    };
    EASMTypes?: {
      [type in FindingType]: string; // lazy translate
    };
    EASMFindingTypesPluralized?: {
      [type in FindingType]: string;
    };
    EASMCountryTypes?: {
      [type in EASMCountryType]: string; // lazy translate
    };
    EASMDNSRecordType?: {
      [type in DNSRecordType]: string; // lazy translate
    };
    nextScanInclusionStatus?: {
      [type in NextScanInclusionStatus]: string; // lazy translate
    };
    expirationStatus?: {
      [type in ExpirationStatus]: string; // lazy translate
    };
    expirationStatusInAssets?: {
      [type in ExpirationStatus]: string; // lazy translate
    };
    integrationNameByType?: {
      [type in IntegrationType]: () => string; // lazy translate
    };
    mdrServiceFlavor?: {
      [mdrFlavor in MdrServiceFlavor]: () => string; // lazy translate
    };
    productServiceStatuses?: {
      [status in ProductServiceStatus]: () => string; // lazy translate
    };
    metadataStatuses?: {
      [status in MetadataStatus]: () => string; // lazy translate
    };
    installationPackagesNames?: {
      [packageName in PackageNameMappings]: string;
    };
    dynamicInstallationPackageNames?: {
      [packageName in PackageNameMappingsHasManageCompanies]: (companyName: string) => string;
    };
    edrResponseActionsTypes?: {
      [type in ResponseActionsType]: string;
    };
    edrResponseActionsActions?: {
      [action in ResponseActionsAction]: string;
    };
    edrResponseActionsTargets?: {
      [target in ResponseActionsTarget]: string;
    };
    edrResponseActionsErrorIds?: {
      [errorId in ResponseActionsErrorId]?: string;
    };
    edrResponseActionsStatuses?: {
      [status in ResponseActionsStatus]: string;
    };
    edrResponseActionsSources?: {
      [source in ResponseActionsSource]: string;
    };
    xdrResponseActionsStatuses?: {
      [status in XdrResponseStatus]: string;
    };
    xdrResponseActionsErrorIds?: {
      [errorId in XdrResponseErrorId]: string;
    };
    customRulesAutomaticActionsTypes?: {
      [type in CustomRulesAutomaticActionsType]: string;
    };
    customRulesAutomaticActionsWarnings?: {
      [type in CustomRulesAutomaticActionsType]?: string;
    };
    customRulesAutomaticActionsDetails?: {
      [type in CustomRulesAutomaticActionsType]: string;
    };
    scanActions?: {
      [status in ScanAction]: string;
    };
    investigationStatuses?: {
      [status in InvestigationStatus]: string;
    };
    investigationAssignedUserTypes?: {
      [type in AssignedUserType]?: string;
    };
    investigationPriorities?: {
      [priority in InvestigationPriority]: string;
    };
  } = {};

  static getInstance(): BaseWebComponentTranslator {
    if (!BaseWebComponentTranslator.instance) {
      BaseWebComponentTranslator.instance = new BaseWebComponentTranslator();
    }
    return BaseWebComponentTranslator.instance;
  }

  public translateFn(str: string, vars?: { [key: string]: string | number }): string {
    return this.dependencies.translateFn(str, vars);
  }

  getInlineActionsCommonStrings() {
    return {
      edit: htmlEncode(this.dependencies.translateFn('Edit')),
      delete: htmlEncode(this.dependencies.translateFn('Delete')),
      rename: htmlEncode(this.dependencies.translateFn('Rename')),
    };
  }

  getCommonStrings() {
    return {
      /**
       * Please keep this on the previous line. We automatically extract texts from translateFn on the same line and upload them to translator tools.
       * If this text is on the previous line (i.e., formatted by the styler), it won't work.
       */
      widgetLoadingError: htmlEncode(
        this.dependencies.translateFn(
          'An internal error prevented the widget to display data. Please contact Bitdefender Enterprise Support for more information.'
        )
      ),
      generalSystemError: htmlEncode(
        this.dependencies.translateFn(
          'A general system error occurred. Please try to refresh the page and if the issue persists contact Bitdefender Enterprise Support for more information.'
        )
      ),
      generalSystemError2: htmlEncode(
        this.dependencies.translateFn(
          'Something went wrong...An internal server error has occurred. Try refreshing the page and if the issue persists, contact Bitdefender Enterprise Support for more information.'
        )
      ),
      noDataAvailable: htmlEncode(
        this.dependencies.translateFn('No data available yet. You can reconfigure the widget.')
      ),
      readOnlyNotice: `${htmlEncode(
        this.dependencies.translateFn(
          'Editing is restricted because the company has declined assistance from their Partner.'
        )
      )} <a href='https://www.bitdefender.com/business/support/en/77211-119560-editing-companies.html'>${htmlEncode(
        this.dependencies.translateFn('Learn more')
      )}</a>`,
      noResultsFound: htmlEncode(this.dependencies.translateFn('No results found.')),
      genericError: htmlEncode(this.dependencies.translateFn('An internal error occurred. Please try again later.')),
      apply: htmlEncode(this.dependencies.translateFn('Apply')),
      search: htmlEncode(this.dependencies.translateFn('Search')),
      more: htmlEncode(this.dependencies.translateFn('More')),
      type: htmlEncode(this.dependencies.translateFn('Type')),
      all: htmlEncode(this.dependencies.translateFn('All')),
      cancel: htmlEncode(this.dependencies.translateFn('Cancel')),
      close: htmlEncode(this.dependencies.translateFn('Close')),
      save: htmlEncode(this.dependencies.translateFn('Save#preserve state')),
      clear: htmlEncode(this.dependencies.translateFn('Clear')),
      saveAs: this.dependencies.translateFn('Save as'),
      discardChanges: this.dependencies.translateFn('Discard changes'),
      update: htmlEncode(this.dependencies.translateFn('Update#action')),
      confirm: htmlEncode(this.dependencies.translateFn('Confirm')),
      custom: htmlEncode(this.dependencies.translateFn('Custom')),
      from: htmlEncode(this.dependencies.translateFn('From')),
      to: htmlEncode(this.dependencies.translateFn('To')),
      copied: htmlEncode(this.dependencies.translateFn('Copied')),
      timeRangeOptions: {
        last24h: htmlEncode(this.dependencies.translateFn('Last 24 hours')),
        last7days: htmlEncode(this.dependencies.translateFn('Last 7 days')),
        last30days: htmlEncode(this.dependencies.translateFn('Last 30 days')),
        allToDate: htmlEncode(this.dependencies.translateFn('All (to date)')),
      },
      loadMore: htmlEncode(this.dependencies.translateFn('Load more')),
      backToTop: htmlEncode(this.dependencies.translateFn('Back to top')),
      requiredField: htmlEncode(this.dependencies.translateFn('Required field')),
      notApplicable: htmlEncode(this.dependencies.translateFn('N/A')),
    };
  }

  getIncidentsSideMenuStrings() {
    return {
      [IncidentType.EDR]: htmlEncode(this.dependencies.translateFn('Endpoint graph')),
      [IncidentType.XDR]: htmlEncode(this.dependencies.translateFn('Organizational graph')),
    };
  }

  getEntitiesSidePanelStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Entities')),
      searchCategoriesPlaceholder: htmlEncode(this.dependencies.translateFn('Search entities')),
    };
  }

  getResourcesSidePanelStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Resources')),
      searchCategoriesPlaceholder: htmlEncode(this.dependencies.translateFn('Search resources')),
    };
  }

  getSchedulerStrings() {
    return {
      weekdays: {
        Sunday: htmlEncode(this.dependencies.translateFn('Sunday')),
        Monday: htmlEncode(this.dependencies.translateFn('Monday')),
        Tuesday: htmlEncode(this.dependencies.translateFn('Tuesday')),
        Wednesday: htmlEncode(this.dependencies.translateFn('Wednesday')),
        Thursday: htmlEncode(this.dependencies.translateFn('Thursday')),
        Friday: htmlEncode(this.dependencies.translateFn('Friday')),
        Saturday: htmlEncode(this.dependencies.translateFn('Saturday')),
        shortNames: {
          Sunday: htmlEncode(this.dependencies.translateFn('Sun')),
          Monday: htmlEncode(this.dependencies.translateFn('Mon')),
          Tuesday: htmlEncode(this.dependencies.translateFn('Tue')),
          Wednesday: htmlEncode(this.dependencies.translateFn('Wed')),
          Thursday: htmlEncode(this.dependencies.translateFn('Thu')),
          Friday: htmlEncode(this.dependencies.translateFn('Fri')),
          Saturday: htmlEncode(this.dependencies.translateFn('Sat')),
        },
      },
      recurrence: {
        IMMEDIATELY: htmlEncode(this.dependencies.translateFn('Immediately')),
        DAILY: htmlEncode(this.dependencies.translateFn('Daily')),
        WEEKLY: htmlEncode(this.dependencies.translateFn('Weekly')),
        MONTHLY: htmlEncode(this.dependencies.translateFn('Monthly')),
      },
      frequency: {
        DAY: htmlEncode(this.dependencies.translateFn('Day')),
        FIRST: htmlEncode(this.dependencies.translateFn('First')),
        SECOND: htmlEncode(this.dependencies.translateFn('Second')),
        THIRD: htmlEncode(this.dependencies.translateFn('Third')),
        FOURTH: htmlEncode(this.dependencies.translateFn('Fourth')),
        LAST: htmlEncode(this.dependencies.translateFn('Last')),
      },
    };
  }

  getDialogStrings() {
    return {
      genericErrorTitle: htmlEncode(this.dependencies.translateFn('Error')),
      genericErrorMessage: htmlEncode(
        this.dependencies.translateFn('An internal error occurred. Please try again later.')
      ),
      loadingErrorTitle: htmlEncode(this.dependencies.translateFn('Loading Error')),
      timeRangeErrorMessage: htmlEncode(
        this.dependencies.translateFn(
          'An internal error occurred when selecting the time interval. Please try again later or contact Bitdefender Enterprise Support if the issue persists.'
        )
      ),
      confirm: htmlEncode(this.dependencies.translateFn('Ok')),
      agree: htmlEncode(this.dependencies.translateFn('Yes')),
      decline: htmlEncode(this.dependencies.translateFn('No')),
    };
  }

  getDashboardsAppStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Executive summary')),
      noCompaniesInTargets: htmlEncode(
        this.dependencies.translateFn(
          'The network group you have access to has no available companies. Please contact your company administrator for more information.'
        )
      ),
    };
  }

  getDashboardStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Executive summary')),
      reportingPeriod: htmlEncode(this.dependencies.translateFn('Reporting period:')),
      actions: htmlEncode(this.dependencies.translateFn('Actions')),
    };
  }

  getOverviewWidgetStrings() {
    return {
      labelsMap: {
        managedEndpoints: htmlEncode(this.dependencies.translateFn('Managed endpoints')),
        activeEndpoints: htmlEncode(this.dependencies.translateFn('Active endpoints')),
        threatsBlocked: htmlEncode(this.dependencies.translateFn('Blocked threats')),
        companyScore: htmlEncode(this.dependencies.translateFn('Company risk score')),
      },
      noEndpointsInstalledForCustomer:
        htmlEncode(this.dependencies.translateFn('You have no managed endpoints yet.')) +
        '<br>' +
        htmlEncode(this.dependencies.translateFn('Download and install security agents.')),
      noEndpointsInstalledForPartner:
        htmlEncode(this.dependencies.translateFn('The security agent is not installed.')) +
        '<br>' +
        htmlEncode(this.dependencies.translateFn('No managed endpoints available.')),
      noEndpointsInstalledTitle: htmlEncode(this.dependencies.translateFn('Secure your systems today')),
      installNowButton: htmlEncode(this.dependencies.translateFn('Install now')),
      sendDownloadLinksButton: htmlEncode(this.dependencies.translateFn('Send download links')),
    };
  }

  getInventoryWidgetStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Inventory')),
      labelsMap: {
        winWorkstations: htmlEncode(this.dependencies.translateFn('Windows workstations')),
        winWorkstationsIcon: htmlEncode(this.dependencies.translateFn('Workstations')),
        winServers: htmlEncode(this.dependencies.translateFn('Windows servers')),
        winServersIcon: htmlEncode(this.dependencies.translateFn('Servers')),
        macOs: htmlEncode(this.dependencies.translateFn('macOS')),
        linux: htmlEncode(this.dependencies.translateFn('Linux')),
        physicalEndpoints: htmlEncode(this.dependencies.translateFn('Physical machines')),
        virtualMachines: htmlEncode(this.dependencies.translateFn('Virtual machines')),
        containerHosts: htmlEncode(this.dependencies.translateFn('Container hosts')),
        containers: htmlEncode(this.dependencies.translateFn('Containers')),
      },
    };
  }

  getTopThreatsWidgetStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Top 5 types of blocked threats')),
    };
  }

  getThreatsBreakdownWidgetStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Threats breakdown by endpoint type')),
      workstationName: htmlEncode(this.dependencies.translateFn('On workstations')),
      serverName: htmlEncode(this.dependencies.translateFn('On servers')),
      containerHostName: htmlEncode(this.dependencies.translateFn('On container hosts')),
      containerName: htmlEncode(this.dependencies.translateFn('On containers')),
    };
  }

  getIncidentsStatusWidgetStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Incidents breakdown by action taken')),
      blockedIncidentsName: htmlEncode(this.dependencies.translateFn('Blocked')),
      investigatingIncidentsName: htmlEncode(this.dependencies.translateFn('Reported')),
      partiallyBlockedName: htmlEncode(this.dependencies.translateFn('Partially blocked')),
    };
  }

  getRemediationsActionsWidgetStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Remediation actions')),
      labels: {
        blocked: htmlEncode(this.dependencies.translateFn('Blocked')),
        deleted: htmlEncode(this.dependencies.translateFn('Deleted')),
        maliciousProcesses: htmlEncode(this.dependencies.translateFn('Malicious processes killed')),
        quarantine: htmlEncode(this.dependencies.translateFn('Moved to quarantine')),
        disinfected: htmlEncode(this.dependencies.translateFn('Disinfected')),
      },
    };
  }

  getEndpointModulesWidgetStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Endpoint modules status')),
      options: {
        enabled: htmlEncode(this.dependencies.translateFn('Enabled')),
        disabled: htmlEncode(this.dependencies.translateFn('Disabled')),
        notInstalled: htmlEncode(this.dependencies.translateFn('Not installed')),
      },
    };
  }

  getEndpointModulesStatusDetailsWidgetStrings() {
    return {
      ...this.getEndpointModulesWidgetStrings(),
      title: htmlEncode(this.dependencies.translateFn('Endpoint modules status details')),
    };
  }

  getCompanyRiskScoreWidgetStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Company risk score')),
      missingYearlyLicense:
        // Do not escape since we have HTML inside.
        this.dependencies.translateFn(
          'The {bold}Endpoint Risk Analytics (ERA){/bold} module is disabled. To retrieve data about your company risk score, access the {bold}Risk Management{/bold} section of your policy and enable the module.',
          {
            bold: '<strong>',
            '/bold': '</strong>',
            interpolation: {
              escapeValue: false,
            },
          }
        ),
      categoriesMap: {
        misconfiguration: this.dependencies.translateFn('Findings'),
        appVulnerabilities: this.dependencies.translateFn('App vulnerabilities'),
        humanRisk: this.dependencies.translateFn('Identity risks'),
      },
    };
  }

  getAccessDataProtectionWidgetStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Policy rule-based detections')),
      columnNames: [
        htmlEncode(this.dependencies.translateFn('Blocked devices')),
        htmlEncode(this.dependencies.translateFn('Blocked connections')),
        htmlEncode(this.dependencies.translateFn('Blocked applications')),
        htmlEncode(this.dependencies.translateFn('Blocked websites')),
      ],
    };
  }

  getWebsitesBlockedWidgetStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Blocked websites')),
      categoriesMap: {
        malware: htmlEncode(this.dependencies.translateFn('Malware')),
        phishing: htmlEncode(this.dependencies.translateFn('Phishing')),
        fraud: htmlEncode(this.dependencies.translateFn('Fraud')),
        other: htmlEncode(this.dependencies.translateFn('Untrusted')),
      },
    };
  }

  getNetworkAttackWidgetStrings() {
    return {
      title: htmlEncode(this.dependencies.translateFn('Blocked network attack techniques')),
      categoriesMap: {
        initialAccess: htmlEncode(this.dependencies.translateFn('Initial access')),
        credentialAccess: htmlEncode(this.dependencies.translateFn('Credential access')),
        discovery: htmlEncode(this.dependencies.translateFn('Discovery')),
        lateralMovement: htmlEncode(this.dependencies.translateFn('Lateral movement')),
        crimeware: htmlEncode(this.dependencies.translateFn('Crimeware')),
      },
    };
  }

  getMyCompanyStrings() {
    return {
      title: this.dependencies.translateFn('My company'),
      companyDetailsTitle: this.dependencies.translateFn('Company Details'),
      authenticationTitle: this.dependencies.translateFn('Authentication'),
      licensingTitle: this.dependencies.translateFn('Licensing'),
      betaProgramsTitle: this.dependencies.translateFn('Early Access'),
      mspInProductTrialsTitle: this.dependencies.translateFn('Products Hub'),
      plansAndPurchase: this.dependencies.translateFn('Purchase'),
    };
  }

  getCompanyDetailsStrings() {
    return {
      companyName: htmlEncode(this.dependencies.translateFn('Company Name')),
      country: htmlEncode(this.dependencies.translateFn('Country')),
    };
  }

  getConfigurationProfilesStrings() {
    return {
      globalExclusionsTitle: htmlEncode(this.dependencies.translateFn('Exclusions')),
      configurationProfilesTitle: htmlEncode(this.dependencies.translateFn('Configuration profiles')),
      maintenanceWindowsTitle: htmlEncode(this.dependencies.translateFn('Maintenance Windows')),
      cloudStagingListTitle: htmlEncode(this.dependencies.translateFn('Update Staging')),
    };
  }

  getGlobalExclusionsStrings() {
    return {
      addExclusions: this.dependencies.translateFn('Add exclusions'),
      assignToLists: this.dependencies.translateFn('Assign to lists'),
      actions: this.dependencies.translateFn('Actions'),
      editList: this.dependencies.translateFn('Edit List'),
      exportExclusions: this.dependencies.translateFn('Export selection'),
      deleteExclusions: this.dependencies.translateFn('Delete'),
      exclusionListsTitle: htmlEncode(this.dependencies.translateFn('Exclusions lists')),
      newList: htmlEncode(this.dependencies.translateFn('New list')),
      allLists: htmlEncode(this.dependencies.translateFn('All Exclusions')),
      allExclusions: htmlEncode(this.dependencies.translateFn('All exclusions')),
      companyExclusionListsTitle: htmlEncode(this.dependencies.translateFn('Other lists')),
      userExclusionListsTitle: htmlEncode(this.dependencies.translateFn('My lists')),
      newExclusionListForm: {
        infoString: htmlEncode(this.dependencies.translateFn('Enter a name and a description to save the list.')),
        descriptionValidationMsg: this.dependencies.translateFn(
          'The description must have a maximum of {nameMaxLength} characters.',
          { nameMaxLength: 128 }
        ),
        nameValidationMsg: this.dependencies.translateFn(
          'The name must have a maximum of {nameMaxLength} characters.',
          {
            nameMaxLength: 64,
          }
        ),
        titleInputLabel: htmlEncode(this.dependencies.translateFn('Name')),
        titleInputPlaceholder: htmlEncode(this.dependencies.translateFn('Name')),
        descriptionInputLabel: htmlEncode(this.dependencies.translateFn('Description')),
        descriptionInputPlaceholder: htmlEncode(this.dependencies.translateFn('type here')),
        importBtnString: htmlEncode(this.dependencies.translateFn('Import')),
        clearBtnString: htmlEncode(this.dependencies.translateFn('Clear')),
      },
      newExclusionItemForm: {
        objectTypeLabel: this.dependencies.translateFn('Object type'),
        objectTypePlaceholder: htmlEncode(this.dependencies.translateFn('Select')),
        excludedItemLabel: htmlEncode(this.dependencies.translateFn('Excluded item')),
        excludedItemPlaceholder: htmlEncode(this.dependencies.translateFn('type here')),
        modulesLabel: htmlEncode(this.dependencies.translateFn('Modules')),
        modulesSearchPlaceholderString: htmlEncode(this.dependencies.translateFn('Select')),
        modulesConfirmActionButtonString: htmlEncode(this.dependencies.translateFn('Apply')),
        modulesNoSearchResultsString: htmlEncode(this.dependencies.translateFn('No results found')),
        modulesListTitle: htmlEncode(this.dependencies.translateFn('All')),
        modulesAllItemsLabel: htmlEncode(this.dependencies.translateFn('All')),
        remarksLabel: htmlEncode(this.dependencies.translateFn('Remarks')),
        remarksPlaceholder: htmlEncode(this.dependencies.translateFn('type here')),
      },
      detailsPanel: {
        createdBy: htmlEncode(this.dependencies.translateFn('Created by')),
        createdOn: htmlEncode(this.dependencies.translateFn('Created on')),
        editedBy: htmlEncode(this.dependencies.translateFn('Edited by')),
        editedOn: htmlEncode(this.dependencies.translateFn('Edited on')),
        details: htmlEncode(this.dependencies.translateFn('Details')),
        general: htmlEncode(this.dependencies.translateFn('General')),
        delete: htmlEncode(this.dependencies.translateFn('Delete')),
        addToPolicy: htmlEncode(this.dependencies.translateFn('Edit assignment')),
      },
    };
  }

  getAntimalwareStrings() {
    return {
      policies: {
        antiTampering: {
          antiTampering: this.dependencies.translateFn('Anti-Tampering'),
          preTampering: this.dependencies.translateFn('Pre-Tampering'),
          preTamperingText: this.dependencies.translateFn(
            "Detects vulnerable drivers on endpoint that can be used to threaten the security agent's integrity. Compatible with Windows and Linux operating systems."
          ),
          vulnerableDrivers: this.dependencies.translateFn('Vulnerable Drivers'),
          denyAccess: this.dependencies.translateFn('Deny access'),
          remediate: this.dependencies.translateFn('Remediate'),
          reportOnly: this.dependencies.translateFn('Report only'),
          postTampering: this.dependencies.translateFn('Post-Tampering'),
          postTamperingText: this.dependencies.translateFn(
            "Detects whether the security agent's callbacks have been maliciously removed or disabled on endpoint, resulting in compromised integrity. Compatible with Windows operating systems."
          ),
          reportCallbackEvasion: this.dependencies.translateFn('Callback Evasion '),
          isolateEndpoint: this.dependencies.translateFn('Isolate endpoint'),
          rebootEndpoint: this.dependencies.translateFn('Reboot endpoint'),
          immediately: this.dependencies.translateFn('Immediately'),
          min5: this.dependencies.translateFn('5 min'),
          min10: this.dependencies.translateFn('10 min'),
          min15: this.dependencies.translateFn('15 min'),
        },
      },
    };
  }

  getIntegrityMonitorStrings() {
    return {
      integrityMonitorEvents: {
        gridTitle: this.dependencies.translateFn('Integrity monitoring events'),
        approveEvent: this.dependencies.translateFn('Approved'),
        unapproveEvent: this.dependencies.translateFn('Unapproved'),
        changeCategory: this.dependencies.translateFn('Change category'),
        allDirectlyManaged: this.dependencies.translateFn('All directly managed'),
      },
      integrityMonitorRules: {
        gridTitle: this.dependencies.translateFn('Integrity monitoring rules'),
        smartViews: {
          panelTitle: this.dependencies.translateFn('Configuration'),
          allRules: this.dependencies.translateFn('All rules'),
          defaultOsRules: this.dependencies.translateFn('Default OS rules'),
          defaultApplicationRules: this.dependencies.translateFn('Default application rules'),
          customRules: this.dependencies.translateFn('Custom rules'),
          allBaselines: this.dependencies.translateFn('All baselines'),
          inlineActions: {
            addToPolicy: this.dependencies.translateFn('Add to policy'),
          },
          emptyRuleSetSmartView: this.dependencies.translateFn('No rule sets saved yet.'),
          deleteViewFormDialogTitle: this.dependencies.translateFn('Delete rule set'),
        },
        defaultRuleConfiguration: {
          needsConfiguration: this.dependencies.translateFn('This rule requires additional configuration.'),
          sourceDeleted: this.dependencies.translateFn('This rule is no longer supported by Bitdefender.'),
          sourceUpdated: this.dependencies.translateFn('This rule was updated by Bitdefender.'),
        },
        actions: this.dependencies.translateFn('Actions'),
        newRule: this.dependencies.translateFn('New rule'),
        newRuleSet: this.dependencies.translateFn('New rule set from rules'),
        assignToRuleSets: this.dependencies.translateFn('Assign rules to rule sets'),
        unassignRules: this.dependencies.translateFn('Unassign rules'),
        deleteRule: this.dependencies.translateFn('Delete'),
        ruleSetForm: {
          save: this.dependencies.translateFn('Save#preserve state'),
          newRuleSet: this.dependencies.translateFn('New rule set'),
          editRuleSet: this.dependencies.translateFn('Edit rule set'),
          name: this.dependencies.translateFn('Rule set name'),
          namePlaceholder: this.dependencies.translateFn('Type here'),
          description: this.dependencies.translateFn('Description'),
          descriptionPlaceholder: this.dependencies.translateFn('Type here'),
          nameIsRequired: this.dependencies.translateFn('Specify a rule set name.'),
          minLengthName: this.dependencies.translateFn('The name must have a minimum of {minLength} characters.', {
            minLength: IMConstants.MIN_LENGTH_RULE_SET_NAME,
          }),
          maxLengthName: this.dependencies.translateFn('The name must have a maximum of {maxLength} characters.', {
            maxLength: IMConstants.MAX_LENGTH_RULE_SET_NAME,
          }),
          specialCharactersNotPermitted: this.dependencies.translateFn('Special characters are not permitted.'),
          nameAlreadyExists: this.dependencies.translateFn(
            'A rule set with the same name already exists. Please specify a different name.'
          ),
          communicationError: this.dependencies.translateFn(
            'A communication error has occurred. Please enter the rule set name again.'
          ),
          maxLengthDescription: this.dependencies.translateFn(
            'The description must have a maximum of {maxLength} characters.',
            {
              maxLength: IMConstants.MAX_LENGTH_RULE_SET_DESCRIPTION,
            }
          ),
        },
        assignRulesForm: {
          title: this.dependencies.translateFn('Assign rules to rule sets'),
          description: this.dependencies.translateFn('Choose the rule sets where to include the selected rules.'),
          assign: this.dependencies.translateFn('Assign'),
          select: this.dependencies.translateFn('Select'),
          search: this.dependencies.translateFn('Search'),
          noRuleSetsFound: this.dependencies.translateFn('No rule sets found.'),
          errorLoadRuleSets: this.dependencies.translateFn('Error loading rule sets'),
          allRuleSets: this.dependencies.translateFn('All rule sets'),
        },
        customRuleForm: {
          save: this.dependencies.translateFn('Save#preserve state'),
          successfulSave: this.dependencies.translateFn('Custom rule created successfully.'),
          successfulUpdate: this.dependencies.translateFn('Custom rule updated successfully.'),
          generalSubtitle: this.dependencies.translateFn('General'),
          nameAlreadyExists: this.dependencies.translateFn(
            'A rule with the same name already exists. Please specify a different name.'
          ),
          communicationError: this.dependencies.translateFn(
            'A communication error has occurred. Please enter the rule name again.'
          ),
          name: this.dependencies.translateFn('Rule name'),
          namePlaceholder: this.dependencies.translateFn('Enter the rule name'),
          nameIsRequired: this.dependencies.translateFn('Specify a rule set name.'),
          minLengthName: this.dependencies.translateFn('The name must have a minimum of {minLength} characters.', {
            minLength: IMConstants.MIN_LENGTH_CUSTOM_RULE_NAME,
          }),
          maxLengthName: this.dependencies.translateFn('The name must have a maximum of {maxLength} characters.', {
            maxLength: IMConstants.MAX_LENGTH_CUSTOM_RULE_NAME,
          }),
          specialCharactersNotPermitted: this.dependencies.translateFn('Special characters are not permitted.'),
          description: this.dependencies.translateFn('Description'),
          descriptionPlaceholder: this.dependencies.translateFn('Enter the rule description'),
          maxLengthDescription: this.dependencies.translateFn(
            'The description must have a maximum of {maxLength} characters.',
            {
              maxLength: IMConstants.MAX_LENGTH_CUSTOM_RULE_DESCRIPTION,
            }
          ),
          severity: this.dependencies.translateFn('Severity'),
          severityPlaceholder: this.dependencies.translateFn('Choose rule severity'),
          entityType: this.dependencies.translateFn('Entity type'),
          entityTypePlaceholder: this.dependencies.translateFn('Choose target entity type'),
          actionsLabel: this.dependencies.translateFn('Monitoring scope'),
          securitySubtitle: this.dependencies.translateFn('Security'),
          osLabel: this.dependencies.translateFn('OS applicability'),
          osPlaceholder: this.dependencies.translateFn('Select'),
          validationErrors: {
            errorDuplicateValueIntroduced: this.dependencies.translateFn('Duplicate value'),
            errorFileRulePrefixIsRequired: this.dependencies.translateFn('Prefix is required.'),
            errorFileRuleExtensionIsRequired: this.dependencies.translateFn(
              'Extension is required if prefix is a directory path.'
            ),
            errorFileRuleInvalidExtension: this.dependencies.translateFn('Invalid extension'),
            errorFileRuleInvalidWildcard: this.dependencies.translateFn(
              'You can only use one wildcard at the end of the prefix.'
            ),
            errorDirectoryRuleEndingSlash: this.dependencies.translateFn('Directory path cannot end with "\\" or "/".'),
            errorPathTooLong: this.dependencies.translateFn(
              'Path is too long. You can enter only {maxLength} characters.',
              {
                maxLength: IMConstants.INTEGRITY_MONITOR_RULE_FILE_MAX_LENGTH_KEY,
              }
            ),
            errorPathRestricted: this.dependencies.translateFn('Path is not allowed.'),
            errorInvalidUnixPath: this.dependencies.translateFn('Invalid Unix path.'),
            errorInvalidWindowsFilePath: this.dependencies.translateFn('Invalid Windows file path.'),
            errorInvalidWindowsDirPath: this.dependencies.translateFn('Invalid Windows directory path.'),
            errorTooManyWildcards: this.dependencies.translateFn('You cannot use wildcards.'),
            errorRegistryKeyRestricted: this.dependencies.translateFn('Registry key is not allowed'),
            errorInvalidRegistryKey: this.dependencies.translateFn('Invalid registry key'),
            errorRegistryKeyTooLong: this.dependencies.translateFn(
              'Key is too long. You can enter only {maxLength} characters.',
              {
                maxLength: IMConstants.INTEGRITY_MONITOR_RULE_REGISTRYKEY_MAX_LENGTH_KEY,
              }
            ),
            errorRegistryValueRestricted: this.dependencies.translateFn('Registry value is not allowed'),
            errorInvalidRegistryValue: this.dependencies.translateFn('Invalid registry value'),
            errorRegistryValueTooLong: this.dependencies.translateFn(
              'Registry value is too long. You can enter only {maxLength} characters.',
              {
                maxLength: IMConstants.INTEGRITY_MONITOR_RULE_REGISTRYVALUE_MAX_LENGTH_VALUE,
              }
            ),
          },
          entityTypeFile: {
            keysLabel: this.dependencies.translateFn('Keys'),
            keysFirstPlaceholder: this.dependencies.translateFn('Prefix'),
            keysSecondPlaceholder: this.dependencies.translateFn('Extensions'),
            errorAtLeastOneKey: this.dependencies.translateFn('Please enter at least one key.'),
            errorTooManyKeys: this.dependencies.translateFn(
              'Too many keys entered. You can enter only {maxKeys} keys.',
              {
                maxKeys: IMConstants.INTEGRITY_MONITOR_RULE_FILE_MAX_ALLOWED_KEYS,
              }
            ),
            errorKeyTooLong: this.dependencies.translateFn(
              'Key is too long. You can enter only {maxLength} characters.',
              {
                maxLength: IMConstants.INTEGRITY_MONITOR_RULE_FILE_MAX_LENGTH_KEY,
              }
            ),
          },
          entityTypeDirectory: {
            pathLabel: this.dependencies.translateFn('Directory path'),
            pathPlaceholder: this.dependencies.translateFn('Type here'),
            includeSubdirectories: this.dependencies.translateFn('Include subdirectories'),
            errorAtLeastOnePath: this.dependencies.translateFn('Please enter at least one path.'),
            errorTooManyPaths: this.dependencies.translateFn(
              'Too many paths entered. You can enter only {maxPaths} paths.',
              {
                maxPaths: IMConstants.INTEGRITY_MONITOR_RULE_DIRECTORY_MAX_ALLOWED_PATHS,
              }
            ),
            errorPathTooLong: this.dependencies.translateFn(
              'Path is too long. You can enter only {maxLength} characters.',
              {
                maxLength: IMConstants.INTEGRITY_MONITOR_RULE_DIRECTORY_MAX_LENGTH_PATH,
              }
            ),
          },
          entityTypeRegistryKey: {
            keyLabel: this.dependencies.translateFn('Registry Key'),
            keyPlaceholder: this.dependencies.translateFn('Type here'),
            errorAtLeastOneKey: this.dependencies.translateFn('Please enter at least one key.'),
            errorTooManyKeys: this.dependencies.translateFn(
              'Too many keys entered. You can enter only {maxKeys} keys.',
              {
                maxKeys: IMConstants.INTEGRITY_MONITOR_RULE_REGISTRYKEY_MAX_ALLOWED_KEYS,
              }
            ),
            errorKeyTooLong: this.dependencies.translateFn(
              'Key is too long. You can enter only {maxLength} characters.',
              {
                maxLength: IMConstants.INTEGRITY_MONITOR_RULE_REGISTRYKEY_MAX_LENGTH_KEY,
              }
            ),
          },
          entityTypeRegistryValue: {
            valueLabel: this.dependencies.translateFn('Registry value'),
            valuePlaceholder: this.dependencies.translateFn('Type here'),
            errorAtLeastOneValue: this.dependencies.translateFn('Please enter at least one value.'),
            errorTooManyValues: this.dependencies.translateFn(
              'Too many values entered. You can enter only {maxValues} values.',
              {
                maxValues: IMConstants.INTEGRITY_MONITOR_RULE_REGISTRYVALUE_MAX_ALLOWED_VALUES,
              }
            ),
            errorValueTooLong: this.dependencies.translateFn(
              'Value is too long. You can enter only {maxLength} characters.',
              {
                maxLength: IMConstants.INTEGRITY_MONITOR_RULE_REGISTRYVALUE_MAX_LENGTH_VALUE,
              }
            ),
          },
        },
      },
      policies: {
        realTime: {
          realTimeMonitoring: this.dependencies.translateFn('Real-time monitoring'),
          ruleProcessingModeTitle: this.dependencies.translateFn('Rules processing mode'),
          ruleProcessingModeLow: this.dependencies.translateFn('Slow'),
          ruleProcessingModeNormal: this.dependencies.translateFn('Normal'),
          ruleProcessingModeHigh: this.dependencies.translateFn('Fast'),
          baseline: this.dependencies.translateFn('Baseline'),
          ruleSets: this.dependencies.translateFn('Rule sets'),
          addRuleSets: this.dependencies.translateFn('Add rule sets'),
          select: this.dependencies.translateFn('Select'),
          search: this.dependencies.translateFn('Search'),
          noRuleSetsFound: this.dependencies.translateFn('No rule sets found.'),
          errorLoadRuleSets: this.dependencies.translateFn('Error loading rule sets'),
          ruleSet: this.dependencies.translateFn('Rule set'),
          rules: this.dependencies.translateFn('Rules'),
          noRuleSetsSelected: this.dependencies.translateFn('No rule sets selected.'),
          allRuleSets: this.dependencies.translateFn('All rule sets'),
        },
      },
    };
  }

  getMdrServiceStrings() {
    return {
      title: 'MDR Service',
      mdrText: this.dependencies.translateFn(
        "When active, the {MDR_ACRONYM} service ensures your company is continuously monitored and protected by Bitdefender's security experts. Threats are detected and addressed in real time, minimizing risk and keeping your business secure 24/7. With onboarding complete, your environment is fully integrated with the {MDR_ACRONYM} service, delivering advanced threat detection and expert guidance for identifying and managing risks and threats.",
        {
          MDR_ACRONYM: 'MDR',
        }
      ),
      openConsole: translateFn('Open console'),
      disabledSsoInfoBox: translateFn(
        'You do not have access to this {MDR} service account. Check your license and your onboarding status or contact support.'
      ),
    };
  }

  getMdrSecurityTelemetryExclusionsStrings() {
    return {
      title: 'MDR Security Telemetry Exclusions'
    }
  }

  getMobileSecurityStrings() {
    return {
      title: translateFn('Mobile security'),
      createAccountText: translateFn(
        "Protect your company's data with Bitdefender Mobile Security. Start using real-time scanning, anti-phishing, anti-spam, anti-theft, and privacy protection for your company's mobile devices.\n"
      ),
      openConsoleText: translateFn(
        "Bitdefender Mobile Security protects your company's mobile devices against a wide range of malicious attacks such as viruses, malware, spyware, and hacking attempts.\nOpen the console to access Mobile Security features.\n"
      ),
      sendActivationLinksText: translateFn(
        "Start sharing activation links and QR codes to manage the protection of your company's mobile devices.\n"
      ),
      createAccount: translateFn('Create account'),
      customerCreateAccountText: translateFn(
        'Create a Mobile Security account for your organization to access all related features. Any charges will be in accordance with your license subscription.'
      ),
      partnerCreateAccountText: translateFn(
        "Create a Mobile Security account for the selected organization and access all related features. Any charges will be in accordance with the account's license subscription."
      ),
      createAccountSuccessToast: translateFn('Account successfully created'),
      createAccountErrorToast: translateFn(
        'Mobile Security account creation failed. Please try again later. If the error persists contact Bitdefender Enterprise support.'
      ),
      openConsole: translateFn('Open console'),
      sendActivationLinks: translateFn('Send activation emails'),
      licenseNotValid: translateFn('License expired or suspended for Mobile Security account.'),
      licenseNotValidWithDaysLeft: (days: number) =>
        translateFn(
          'License expired or suspended for Mobile Security account. The Mobile Security console will be removed in {days} days.',
          { days }
        ),
      licenseNotValidExpiringToday: translateFn(
        'License expired or suspended for Mobile Security account. The Mobile Security console will be removed today.'
      ),
      cancel: translateFn('Cancel'),
      accountCreatedToast: translateFn('Account successfully created'),
      activationEmails: {
        info: translateFn('Certain features and services may come with additional costs.'),
        placeholder: translateFn('You can add up to 100 email addresses.'),
        emailsInputLabel: translateFn('Send email invites for multiple installs'),
        invalidEmailMessage: translateFn(
          'The email addresses provided must be valid, unique, and separated by a comma.'
        ),
        limitReached: translateFn('You have reached the limit of 100 email addresses.'),
        learnMore: translateFn('Learn more.'),
      },
    };
  }

  getCloudWorkloadSecurity() {
    return {
      title: translateFn('Cloud Security'),
      createAccountText: translateFn(
        'Keep your cloud resources safe with GravityZone CSPM+. Quickly onboard your cloud inventory, identify risks, and report on your adherence to renowned compliance frameworks.'
      ),
      createAccount: translateFn('Create account'),
      customerCreateAccountText: (companyName: string) =>
        translateFn('This will create a Cloud Security account for the company: {companyName}.', { companyName }),
      createAccountSuccessToast: translateFn('Account successfully created'),
      createAccountErrorToast: translateFn(
        'Cloud Security account creation failed. Please try again later. If the error persists contact Bitdefender Enterprise support.'
      ),
      openConsoleText: translateFn(
        'Keep your cloud resources safe with GravityZone CSPM+. Quickly onboard your cloud inventory, identify risks, and report on your adherence to renowned compliance frameworks.'
      ),
      openConsole: translateFn('Open console'),
      licenseNotValid: translateFn('License expired or suspended for Cloud Security account.'),
      licenseNotValidWithDaysLeft: (days: number) =>
        translateFn(
          'Account suspended or license expired. Your account will be removed in {days} days. Contact your account manager for more information.',
          { days }
        ),
      cancel: translateFn('Cancel'),
    };
  }

  getXdrSearchStrings() {
    return {
      invalidQuery: htmlEncode(this.dependencies.translateFn('Invalid query or unsupported syntax')),
      invalidField: htmlEncode(this.dependencies.translateFn('Unknown field')),
      invalidOperator: htmlEncode(this.dependencies.translateFn('Unknown operator')),
      missingValue: htmlEncode(this.dependencies.translateFn('Missing value')),
      invalidValue: htmlEncode(this.dependencies.translateFn('Invalid value')),
      missingOperator: htmlEncode(this.dependencies.translateFn('Missing operator')),
      titleQueryPanel: htmlEncode(this.dependencies.translateFn('Search')),
      titleLiveQueryPanel: this.dependencies.translateFn('New query'),
      queryBtnString: htmlEncode(this.dependencies.translateFn('Run Query')),
      clearBtnString: htmlEncode(this.dependencies.translateFn('Clear')),
      hintTextString: htmlEncode(
        this.dependencies.translateFn(
          'Press Ctrl+/ to open Helper. Press Ctrl+Space to open Autocomplete. Press Enter to run the query'
        )
      ),
      queryInputPlaceholder: htmlEncode(this.dependencies.translateFn('Type your query')),
      datetimeFilterLabel: htmlEncode(this.dependencies.translateFn('Date')),
      detailsPanel: {
        detectedBy: htmlEncode(this.dependencies.translateFn('Detected by')),
        detectedOn: htmlEncode(this.dependencies.translateFn('Detected on')),
        details: htmlEncode(this.dependencies.translateFn('Details')),
        type: htmlEncode(this.dependencies.translateFn('Type')),
        severity: htmlEncode(this.dependencies.translateFn('Severity')),
        severityLevel: {
          high: htmlEncode(this.dependencies.translateFn('High')),
          medium: htmlEncode(this.dependencies.translateFn('Medium')),
          low: htmlEncode(this.dependencies.translateFn('Low')),
        },
        table: htmlEncode(this.dependencies.translateFn('Table')),
        json: 'JSON',
        showMore: htmlEncode(this.dependencies.translateFn('Show more')),
        showLess: htmlEncode(this.dependencies.translateFn('Show less')),
        emptyJsonText: this.dependencies.translateFn('No JSON data available'),
        copyBtnString: this.dependencies.translateFn('Copy to clipboard'),
        confirmationCopy: this.dependencies.translateFn('Copied'),
      },
      savedTextString: htmlEncode(this.dependencies.translateFn('Saved')),
      historicalGzTab: this.dependencies.translateFn('Historical'),
      liveGzTab: this.dependencies.translateFn('Live'),
      searchTabGroup: this.dependencies.translateFn('Search'),
      savedQueriesTextString: this.dependencies.translateFn('Saved'),
      recentQueriesTextString: this.dependencies.translateFn('Recent'),
      predefinedQueriesTextString: this.dependencies.translateFn('Predefined'),
      newQueryItem: this.dependencies.translateFn('New query'),
    };
  }

  public translateDomainControllerRoles(role: DomainControllerRoleEnum): string {
    switch (role) {
      case DomainControllerRoleEnum.CERTIFICATE_AUTHORITY:
        return this.dependencies.translateFn('Certificate Authority');
      case DomainControllerRoleEnum.DOMAIN_CONTROLLER:
        return this.dependencies.translateFn('Domain Controller');
      default:
        return '-';
    }
  }

  public translateProductId(licenseProductId: LicenseProductIdEnum): string {
    if (!this.translations.licenseProductId) {
      this.translations.licenseProductId = {
        [LicenseProductIdEnum.ProductIdCeps]: () => 'Bitdefender Small Office Security (Cloud Console)',
        [LicenseProductIdEnum.ProductIdVifor]: () => 'Cloud Security for Endpoints',
        [LicenseProductIdEnum.ProductIdEpcAdvanced]: () => 'GravityZone Physical Endpoints Advanced',
        [LicenseProductIdEnum.ProductIdEpcDesktops]: () => 'GravityZone Security for Endpoints Physical Workstations',
        [LicenseProductIdEnum.ProductIdEpcDesktopsExtended]: () =>
          'GravityZone Security for Endpoints Physical Workstations',
        [LicenseProductIdEnum.ProductIdEpcServers]: () => 'GravityZone Security for Endpoints Physical Servers',
        [LicenseProductIdEnum.ProductIdEpcServersExtended]: () => 'GravityZone Security for Endpoints Physical Servers',
        [LicenseProductIdEnum.ProductIdGlobal]: () => 'GravityZone-in-a-box',
        [LicenseProductIdEnum.ProductIdSos]: () => 'Bitdefender Small Office Security (On-premise Console)',
        [LicenseProductIdEnum.ProductIdMdm]: () => 'GravityZone Security for Mobile Devices',
        [LicenseProductIdEnum.ProductIdSveCpu]: () => 'GravityZone Security per CPU license',
        [LicenseProductIdEnum.ProductIdSveCpuExtended]: () => 'GravityZone Security for Virtual Env per CPU',
        [LicenseProductIdEnum.ProductIdSveVdi]: () => 'GravityZone Security for VDI',
        [LicenseProductIdEnum.ProductIdSveVdiExtended]: () => 'GravityZone Security for Virtualized Environments VDI',
        [LicenseProductIdEnum.ProductIdSveVs]: () => 'GravityZone Security for Virtual Servers',
        [LicenseProductIdEnum.ProductIdSveVsExtended]: () => 'GravityZone Security for Virtualized Environments VS',
        [LicenseProductIdEnum.ProductIdExchangeServers]: () => 'GravityZone Security for Exchange Servers',
        [LicenseProductIdEnum.ProductIdOfficeSecurityPack]: () => 'GravityZone Business Security',
        [LicenseProductIdEnum.ProductIdBusinessSecurityPack]: () => 'GravityZone Advanced Business Security',
        [LicenseProductIdEnum.ProductIdEnterprisePoolsAbsPack]: () => 'GravityZone Advanced Business Security',
        [LicenseProductIdEnum.ProductIdNextGen]: () => 'GravityZone Business Security Premium',
        [LicenseProductIdEnum.ProductIdNextGenExtended]: () => 'GravityZone Elite',
        [LicenseProductIdEnum.ProductIdAtd]: () => 'GravityZone Business Security Enterprise',
        [LicenseProductIdEnum.ProductIdEarlyAccess]: () => 'GravityZone XDR - Early Access Program',
        [LicenseProductIdEnum.ProductIdRemoteAccess]: () => 'GravityZone XDR - Remote Shell',
        [LicenseProductIdEnum.ProductIdVolumeEncryption]: () => 'GravityZone Full Disk Encryption',
        [LicenseProductIdEnum.ProductIdPatchManagement]: () => 'GravityZone Patch Management',
        [LicenseProductIdEnum.ProductIdStorageProtection]: () => 'GravityZone Security for Storage',
        [LicenseProductIdEnum.ProductIdHdSandbox]: () => 'GravityZone HD Add-on (HyperDetect, Sandbox)',
        [LicenseProductIdEnum.ProductIdSandboxLocal]: () => 'GravityZone Sandbox Analyzer On Premises',
        [LicenseProductIdEnum.ProductIdNtsaServers]: () => 'Network Traffic Security Analytics (NTSA)',
        [LicenseProductIdEnum.ProductIdNtsaClientDevices]: () => 'Network Traffic Security Analytics (NTSA)',
        [LicenseProductIdEnum.ProductIdNtsaThroughput]: () => 'Network Traffic Security Analytics (NTSA)',
        [LicenseProductIdEnum.ProductIdContainerProtection]: () => 'GravityZone Security for Containers',
        [LicenseProductIdEnum.ProductIdEdrHybridAddon]: () => 'GravityZone Endpoint Detection and Response',
        [LicenseProductIdEnum.ProductIdEmailSecurity]: () => 'GravityZone Security for Email',
        [LicenseProductIdEnum.ProductIdMobileSecurity]: () => 'GravityZone Security for Mobile',
        [LicenseProductIdEnum.ProductIdEdrEs30DaysStorage]: () => 'GravityZone EDR 30 days Data Retention Add-on',
        [LicenseProductIdEnum.ProductIdEdrEs90DaysStorage]: () => 'GravityZone EDR 90 days Data Retention Add-on',
        [LicenseProductIdEnum.ProductIdEdrEs180DaysStorage]: () => 'GravityZone EDR 180 days Data Retention Add-on',
        [LicenseProductIdEnum.ProductIdEdrEs365DaysStorage]: () => 'GravityZone EDR 365 days Data Retention Add-on',
        [LicenseProductIdEnum.ProductIdCompanion]: () => 'GravityZone EDR Cloud',
        [LicenseProductIdEnum.ProductIdMsp]: () => 'Cloud Security for MSP',
        [LicenseProductIdEnum.ProductIdFrat]: () => 'Free Risk Assessment Tool',
        [LicenseProductIdEnum.ProductIdDesktopSecurity]: () => 'GravityZone Security for Workstations',
        [LicenseProductIdEnum.ProductIdServerSecurity]: () => 'GravityZone Security for Servers',
        [LicenseProductIdEnum.ProductIdXdrProductivitySensors]: () => 'Bitdefender XDR Sensor - Productivity',
        [LicenseProductIdEnum.ProductIdXdrCloudSensors]: () => 'Bitdefender XDR Sensor - Cloud',
        [LicenseProductIdEnum.ProductIdXdrIdentitySensors]: () => 'Bitdefender XDR Sensor - Identity',
        [LicenseProductIdEnum.ProductIdXdrSensitiveDataSensors]: () => 'Bitdefender XDR Sensor - Business Applications',
        [LicenseProductIdEnum.ProductIdXdrNetworkSensors]: () => 'Bitdefender XDR Sensor - Network',
        [LicenseProductIdEnum.ProductIdIntegrityMonitorAddon]: () => 'GravityZone Integrity Monitoring',
        [LicenseProductIdEnum.ProductIdIntegrityMonitorEs90DaysStorage]: () =>
          'GravityZone Integrity Monitoring 90 days Data Retention Add-on',
        [LicenseProductIdEnum.ProductIdIntegrityMonitorEs180DaysStorage]: () =>
          'GravityZone Integrity Monitoring 180 days Data Retention Add-on',
        [LicenseProductIdEnum.ProductIdIntegrityMonitorEs365DaysStorage]: () =>
          'GravityZone Integrity Monitoring 365 days Data Retention Add-on',
        [LicenseProductIdEnum.ProductIdCoreOnlineProduct]: () => 'GravityZone Small Business Security',
        [LicenseProductIdEnum.ProductIdNadAndEraAddon]: () => 'Network Attack Defense and Risk Management',
        [LicenseProductIdEnum.ProductIdWebAccessAndDeviceControlAddon]: () => 'Web Access and Device Control',
        [LicenseProductIdEnum.ProductIdMdr]: () => 'Bitdefender MDR',
        [LicenseProductIdEnum.ProductIdMdrPremium]: () => 'MDR Premium',
        [LicenseProductIdEnum.ProductIdMdrPlus]: () => 'Bitdefender MDR Plus',
        [LicenseProductIdEnum.ProductIdBreachWarranty]: () => 'MDR Breach Warranty',
        [LicenseProductIdEnum.ProductIdBreachWarrantyPlus]: () => 'MDR Breach Warranty Plus',
        [LicenseProductIdEnum.ProductIdBreachWarrantyPlusOver1000]: () => 'MDR Breach Warranty Plus',
        [LicenseProductIdEnum.ProductIdCws]: () => 'GravityZone CSPM+',
        [LicenseProductIdEnum.ProductIdComplianceManager]: () => 'GravityZone Compliance Manager',
        [LicenseProductIdEnum.ProductIdPhasrAddon]: () => 'Proactive Hardening and Attack Surface Reduction',
        [LicenseProductIdEnum.ProductIdExposureManagement]: () => 'GravityZone External Attack Surface Management',
        [LicenseProductIdEnum.ProductIdXdrBundle]: () => 'Bitdefender GravityZone Defense XDR',
      };

      this.translations.licenseProductId = PRODUCT_ID_TO_NAME;
    }

    return this.translations.licenseProductId[licenseProductId] != null
      ? this.translations.licenseProductId[licenseProductId]()
      : this.dependencies.translateFn('N/A');
  }

  public getLicenseProductUnit(licenseProductId: number): string {
    if (!this.translations.licenseUnitMapping) {
      this.translations.licenseUnitMapping = {
        [LicenseProductIdEnum.ProductIdCeps]: () => this.dependencies.translateFn('entities'),
        [LicenseProductIdEnum.ProductIdMsp]: () => this.dependencies.translateFn('Seats'),
        [LicenseProductIdEnum.ProductIdEpcDesktopsExtended]: () =>
          this.dependencies.translateFn('physical workstations'),
        [LicenseProductIdEnum.ProductIdEpcServersExtended]: () => this.dependencies.translateFn('physical servers'),
        [LicenseProductIdEnum.ProductIdGlobal]: () => this.dependencies.translateFn('Entities'),
        [LicenseProductIdEnum.ProductIdSveVdiExtended]: () => this.dependencies.translateFn('virtual desktops'),
        [LicenseProductIdEnum.ProductIdSveVsExtended]: () => this.dependencies.translateFn('virtual servers'),
        [LicenseProductIdEnum.ProductIdContainerProtection]: () => this.dependencies.translateFn('container hosts'),
        [LicenseProductIdEnum.ProductIdEmailSecurity]: () => this.dependencies.translateFn('mailboxes'),
        [LicenseProductIdEnum.ProductIdMobileSecurity]: () => this.dependencies.translateFn('mobile devices'),
        [LicenseProductIdEnum.ProductIdDesktopSecurity]: () => this.dependencies.translateFn('workstations'),
        [LicenseProductIdEnum.ProductIdServerSecurity]: () => this.dependencies.translateFn('servers'),
        [LicenseProductIdEnum.ProductIdXdrProductivitySensors]: () => this.dependencies.translateFn('Mailboxes'),
        [LicenseProductIdEnum.ProductIdXdrCloudSensors]: () => this.dependencies.translateFn('users'),
        [LicenseProductIdEnum.ProductIdXdrIdentitySensors]: () => this.dependencies.translateFn('users'),
        [LicenseProductIdEnum.ProductIdXdrSensitiveDataSensors]: () => this.dependencies.translateFn('users'),
        [LicenseProductIdEnum.ProductIdXdrNetworkSensors]: () => this.dependencies.translateFn('users'),
        [LicenseProductIdEnum.ProductIdCws]: () => this.dependencies.translateFn('cloud resources'),
      };
    }

    return this.translations.licenseUnitMapping[licenseProductId] != null
      ? this.translations.licenseUnitMapping[licenseProductId]()
      : this.dependencies.translateFn('endpoints');
  }

  public getYearlyProductType(isAddon: boolean) {
    return isAddon ? this.dependencies.translateFn('Add-on') : this.dependencies.translateFn('Standard');
  }

  public getMonthlyProductType(productType: MonthlyProductType): string {
    if (!this.translations.licenseProductType) {
      this.translations.licenseProductType = {
        [MonthlyProductType.EndpointSecurity]: () => 'Endpoint Security',
        [MonthlyProductType.BitdefenderEdr]: () => 'Bitdefender EDR',
      };
    }

    return this.translations.licenseProductType[productType] !== null
      ? this.translations.licenseProductType[productType]()
      : this.dependencies.translateFn('N/A');
  }

  getPoliciesStrings() {
    return {
      policiesSelector: {
        policies: htmlEncode(this.dependencies.translateFn('Policies')),
      },
    };
  }

  getMaintenanceWindowsStrings() {
    return {
      addWindow: htmlEncode(this.dependencies.translateFn('Add window')),
      deleteWindow: htmlEncode(this.dependencies.translateFn('Delete')),
      allMaintenanceWindows: htmlEncode(this.dependencies.translateFn('All maintenance windows')),
      maintenanceType: htmlEncode(this.dependencies.translateFn('Maintenance type')),
    };
  }

  public getTranslatedSubscriptionType(subscriptionType: SubscriptionType): string {
    if (!this.translations.subscriptionType) {
      this.translations.subscriptionType = {
        [SubscriptionType.None]: () => this.dependencies.translateFn('None'),
        [SubscriptionType.Trial]: () => this.dependencies.translateFn('Trial'),
        [SubscriptionType.Licensed]: () => this.dependencies.translateFn('License'),
        [SubscriptionType.Inherited]: () => this.dependencies.translateFn('Monthly Subscription'),
        [SubscriptionType.MonthlyLicenseTrial]: () => this.dependencies.translateFn('Monthly License Trial'),
        [SubscriptionType.InheritedMspTrial]: () => this.dependencies.translateFn('Monthly Subscription Trial'),
        [SubscriptionType.Frat]: () => this.dependencies.translateFn('FRAT Subscription'),
        [SubscriptionType.BasicMonthly]: () => this.dependencies.translateFn('Basic Monthly Subscription'),
      };
    }

    return this.translations.subscriptionType[subscriptionType]();
  }

  getSensorSetupText(integrationType?: IntegrationType): string {
    if (!integrationType) {
      return this.dependencies.translateFn('Sensor setup');
    }
    return this.dependencies.translateFn('Sensor setup - {sensorName}', {
      sensorName: this.getTranslatedIntegrationNameByType(integrationType),
    });
  }

  getRiskIncidentPanelStrings(): object {
    return {
      rootCauseRisks: this.dependencies.translateFn('Root cause risks'),
      riskManagement: this.dependencies.translateFn('Risk Management'),
      cloudSecurity: this.dependencies.translateFn('Cloud Security'),
      endpointRisks: this.dependencies.translateFn('Endpoint risks'),
      cloudRisks: this.dependencies.translateFn('Cloud risks'),
      ruleLinkSidePanelText: this.dependencies.translateFn('View finding in Cloud Security console'),
      nodeLinkSidePanelText: this.dependencies.translateFn('View resource in Cloud Security console'),
      ruleLinkRiskManagementSidePanelText: this.dependencies.translateFn('View finding in Risk Management section'),
      nodeLinkRiskManagementSidePanelText: this.dependencies.translateFn('View resource in Risk Management section'),
      pivotToVulnerabilityText: this.dependencies.translateFn('View vulnerability'),
      pivotToDeviceText: this.dependencies.translateFn('View resource in Risk Management section'),
      pivotToMisconfigurationText: this.dependencies.translateFn('View finding in Risk Management section'),
      viewAllRisksText: this.dependencies.translateFn('View all risks'),
      subtitlePanelText: this.dependencies.translateFn('Distribution by impacted entity types'),
      donutChartTitle: this.dependencies.translateFn('risks'),
      noDataAvailableWithCspmLicense: this.dependencies.translateFn(
        'No data available for Root cause risks, Top endpoint risks, and Top cloud risks'
      ),
      noDataAvailableWithoutCspmLicense: this.dependencies.translateFn(
        'No data available for Root cause risks and Top endpoint risks'
      ),
      getLicenseText: this.dependencies.translateFn('Get license'),
      errorLoadingData: this.dependencies.translateFn(
        'Error loading data for Root cause risks, Top endpoint risks, and Top cloud risks'
      ),
      panelTitle: this.dependencies.translateFn('Associated risks'),
      cloudSecurityLicenseRequired: this.dependencies.translateFn('CSPM+ license required'),
      reasonForCloudSecurityLicenseRequired: this.dependencies.translateFn(
        'Add a license for CSPM+ and configure the Cloud Security console to expand incident context with cloud security posture data.'
      ),
    };
  }

  public getSensorsIntegrationsStrings(): object {
    return {
      noResultsMessage: this.dependencies.translateFn('Start adding sensors'),
      noResultsAdditionalText: this.dependencies.translateFn("You don't have any sensors here yet"),
      noResultsAddNewButtonLabel: this.dependencies.translateFn('Add New'),
      unknownName: this.dependencies.translateFn('Unknown'),
      cloudSensor: this.dependencies.translateFn('Cloud Sensor'),
      networkSensor: this.dependencies.translateFn('Network Sensor'),
      identitySensor: this.dependencies.translateFn('Identity Sensor'),
      productivitySensor: this.dependencies.translateFn('Productivity Sensor'),
      mobileSecuritySensor: this.dependencies.translateFn('Security for Mobile'),
      cloudSecuritySensor: this.dependencies.translateFn('GravityZone Cloud Security'),
      sensitiveDataSensor: this.dependencies.translateFn('Business Applications'),
      integrationDescription: {
        [IntegrationType.AZURE_CW]: this.dependencies.translateFn(
          'The Azure Cloud sensor collects and pre-processes cloud activity data.'
        ),
        [IntegrationType.O365]: this.dependencies.translateFn(
          'The Mail and Audit sensors collect and pre-process data about email traffic and content, as well as user and admin operations retrieved from the Microsoft 365 unified audit log.'
        ),
        [IntegrationType.AD_ONPREM]: this.dependencies.translateFn(
          'The Active Directory sensor collects and processes user login information from the on-premises Active Directory.'
        ),
        [IntegrationType.AWS]: this.dependencies.translateFn(
          'The AWS sensor collects and processes information about configuration changes and actions taken by users, roles, or AWS services.'
        ),
        [IntegrationType.AZURE_AD]: this.dependencies.translateFn(
          'The Azure AD sensor collects and pre-processes data related to user sign in activity, as well as configuration changes related to users and groups.'
        ),
        [IntegrationType.NSVA]: this.dependencies.translateFn(
          'The Network sensor collects and pre-processes network-related events in order to enrich the context of your incidents.'
        ),
        [IntegrationType.INTUNE]: this.dependencies.translateFn(
          'The Microsoft Intune sensor collects and pre-processes device-related data.'
        ),
        [IntegrationType.GOOGLE_WS]: this.dependencies.translateFn(
          'The Google Workspace sensor collects and pre-processes activity and usage data related to Google Workspace accounts and services.'
        ),
        [IntegrationType.GCP]: this.dependencies.translateFn(
          'The Google Cloud Platform sensor collects and processes audit information related to one Google Cloud project of your choice.'
        ),
        [IntegrationType.MOBILE_SECURITY]: this.dependencies.translateFn(
          'The Security for Mobile sensor processes mobile device events collected from GravityZone Security for Mobile.'
        ),
        [IntegrationType.CLOUD_SECURITY]: this.dependencies.translateFn(
          'The CSPM+ sensor collects and processes information about your cloud security posture from GravityZone Cloud Security to enrich the context of your incidents.'
        ),
        [IntegrationType.ATLASSIAN_CLOUD]: this.dependencies.translateFn(
          'The Atlassian Cloud sensors collect and process security and activity information to monitor Atlassian cloud-based products.'
        ),
      },
      sendFeedback: {
        feedbackName: this.dependencies.translateFn('Need a different sensor?'),
        feedbackDescription: this.dependencies.translateFn('Tell us which:'),
        feedbackHoverDescription: this.dependencies.translateFn(
          'Send us your suggestions ({maxLength} characters max).',
          {
            maxLength: SendFeedbackEmailConstants.Input.MaxLength,
          }
        ),
        feedbackButton: this.dependencies.translateFn('Send'),
      },
      integrationStatusLabels: {
        [IntegrationStatus.ACTIVE]: this.dependencies.translateFn('Active'),
        [IntegrationStatus.ACTION_NEEDED]: this.dependencies.translateFn('Action needed'),
        [IntegrationStatus.DISABLED]: this.dependencies.translateFn('Disabled'),
        [IntegrationStatus.NOT_LICENSED]: this.dependencies.translateFn('Not licensed'),
        [IntegrationStatus.ACTION_NEEDED_YELLOW]: this.dependencies.translateFn('Action needed'),
      },
      integrationResponseStatusLabels: {
        [IntegrationResponseStatus.ACTIVE]: this.dependencies.translateFn('Active'),
        [IntegrationResponseStatus.NOT_AVAILABLE]: this.dependencies.translateFn('Action needed'),
        [IntegrationResponseStatus.UNKNOWN]: this.dependencies.translateFn('N/A'),
        [IntegrationResponseStatus.DISABLED]: this.dependencies.translateFn('Disabled'),
      },
      sensorNames: {
        [SensorType.O365_AUDIT]: this.dependencies.translateFn('Audit'),
        [SensorType.O365_MAIL]: this.dependencies.translateFn('Mail'),
        [SensorType.AD_ONPREM_DOMAIN]: this.dependencies.translateFn('Active Directory'),
        [SensorType.AWS]: this.dependencies.translateFn('AWS'),
        [SensorType.AZURE_AD_AUDIT]: this.dependencies.translateFn('Azure AD'),
        [SensorType.AZURE_CW]: this.dependencies.translateFn('Azure Cloud'),
        [SensorType.NSVA]: this.dependencies.translateFn('Network Sensor'),
        [SensorType.INTUNE]: this.dependencies.translateFn('Microsoft Intune'),
        [SensorType.GOOGLE_WS]: this.dependencies.translateFn('Google Workspace'),
        [SensorType.GCP]: this.dependencies.translateFn('Google Cloud Platform'),
        [SensorType.MOBILE_SECURITY]: this.dependencies.translateFn('Security for Mobile'),
        [SensorType.CLOUD_SECURITY]: this.dependencies.translateFn('CSPM+'),
        [SensorType.ATLASSIAN_AUDIT]: this.dependencies.translateFn('Atlassian Admin'),
        [SensorType.ATLASSIAN_CONFLUENCE]: this.dependencies.translateFn('Confluence'),
        [SensorType.ATLASSIAN_JIRA]: this.dependencies.translateFn('Jira'),
        [SensorType.AZURE_AD_EVENT_HUB]: this.dependencies.translateFn('Azure AD'),
      },
      unknownStatusLabel: this.dependencies.translateFn('Unknown'),
      descriptionLabel: this.dependencies.translateFn('General'),
      detailsLabel: this.dependencies.translateFn('Details'),
      companyLabel: this.dependencies.translateFn('Company'),
      unknownSensorTypeLabel: this.dependencies.translateFn('Unknown'),
      sensorTypeLabel: this.dependencies.translateFn('Sensor type'),
      networkNameLabel: this.dependencies.translateFn('Network name'),
      subnetLabel: this.dependencies.translateFn('Subnet'),
      interfaceLabel: this.dependencies.translateFn('Interface'),
      sensorErrorLabel: this.dependencies.translateFn('Reason'),
      enabledInPolicyLabel: this.dependencies.translateFn('Enabled in policy'),
      sensorStatusLabel: this.dependencies.translateFn('Sensor status'),
      statusLabel: this.dependencies.translateFn('Status'),
      responseStatusLabel: this.dependencies.translateFn('Response status'),
      sensorStatusLabels: {
        [SensorStatus.ACTIVE]: this.dependencies.translateFn('Active'),
        [SensorStatus.OFFLINE]: this.dependencies.translateFn('Offline'),
        [SensorStatus.DISABLED]: this.dependencies.translateFn('Disabled'),
        [SensorStatus.NOT_CONFIGURED]: this.dependencies.translateFn('Not configured'),
        [SensorStatus.ACTION_NEEDED]: this.dependencies.translateFn('Action needed'),
        [SensorStatus.BAD_AUDIT_POLICY_CONFIG]: this.dependencies.translateFn('Not configured'),
        [SensorStatus.BAD_CERTIFICATE_AUDIT_POLICY_CONFIG]: this.dependencies.translateFn('Not configured'),
        [SensorStatus.BEST_NOT_INSTALLED]: this.dependencies.translateFn('Not configured'),
        [SensorStatus.RESTART_NEEDED]: this.dependencies.translateFn('Restart required'),
        [SensorStatus.NOT_LICENSED]: this.dependencies.translateFn('Not licensed'),
        [SensorStatus.UNKNOWN]: this.dependencies.translateFn('Unknown'),
      },
      sensorResponseLabels: {
        [SensorResponseStatus.ACTIVE]: this.dependencies.translateFn('Active'),
        [SensorResponseStatus.NOT_AVAILABLE]: this.dependencies.translateFn('Action needed'),
        [SensorResponseStatus.UNKNOWN]: this.dependencies.translateFn('N/A'),
        [SensorResponseStatus.DISABLED]: this.dependencies.translateFn('Disabled'),
      },
      unknownSensorStatusLabel: this.dependencies.translateFn('Unknown'),
      disableSensorActionButtonLabel: this.dependencies.translateFn('Disable sensor'),
      enableSensorActionButtonLabel: this.dependencies.translateFn('Enable sensor'),
      disableActionButtonLabel: this.dependencies.translateFn('Disable'),
      enableActionButtonLabel: this.dependencies.translateFn('Enable'),
      addLicenseActionButtonLabel: this.dependencies.translateFn('Add license'),
      sensorDisableDialogTitle: this.dependencies.translateFn('Disable sensor?'),
      sensorDisableDialogText: this.dependencies.translateFn('This sensor will no longer process data.'),
      disableDialogText: {
        [IntegrationType.O365]: this.dependencies.translateFn('This sensor integration will no longer process data.'),
        [IntegrationType.AD_ONPREM]: this.dependencies.translateFn(
          'This sensor integration will no longer process data.'
        ),
        [IntegrationType.AWS]: this.dependencies.translateFn('This sensor integration will no longer process data.'),
        [IntegrationType.AZURE_AD]: this.dependencies.translateFn(
          'This sensor integration will no longer process data.'
        ),
        [IntegrationType.AZURE_CW]: this.dependencies.translateFn(
          'The sensor integration will no longer process data.'
        ),
        [IntegrationType.NSVA]: this.dependencies.translateFn('The sensor will no longer process data.'),
        [IntegrationType.INTUNE]: this.dependencies.translateFn('The sensor integration will no longer process data.'),
        [IntegrationType.GOOGLE_WS]: this.dependencies.translateFn(
          'The sensor integration will no longer process data.'
        ),
        [IntegrationType.GCP]: this.dependencies.translateFn('The sensor integration will no longer process data.'),
        [IntegrationType.MOBILE_SECURITY]: this.dependencies.translateFn(
          'The sensor integration will no longer process data.'
        ),
        [IntegrationType.CLOUD_SECURITY]: this.dependencies.translateFn(
          'The sensor integration will no longer process data.'
        ),
        [IntegrationType.ATLASSIAN_CLOUD]: this.dependencies.translateFn(
          'The sensor integration will no longer process data.'
        ),
      },
      setEnabledStatusDialogCancelButtonLabel: this.dependencies.translateFn('Cancel'),
      disableDialogConfirmButtonLabel: this.dependencies.translateFn('Disable'),
      deleteActionButtonLabel: this.dependencies.translateFn('Delete'),
      deleteDialogText: {
        [IntegrationType.O365]: this.dependencies.translateFn(
          'This sensor integration setup will be removed permanently.'
        ),
        [IntegrationType.AD_ONPREM]: this.dependencies.translateFn(
          'The sensor will no longer process data and it will be removed from the Sensor Management list.'
        ),
        [IntegrationType.AWS]: this.dependencies.translateFn(
          'The sensor will no longer process data and it will be removed from the Sensor Management list.'
        ),
        [IntegrationType.AZURE_AD]: this.dependencies.translateFn(
          'The sensor will no longer process data and it will be removed from the Sensor Management list.'
        ),
        [IntegrationType.AZURE_CW]: this.dependencies.translateFn(
          'The sensor will no longer process data and it will be removed from the Sensor Management list.'
        ),
        [IntegrationType.NSVA]: this.dependencies.translateFn(
          'The sensor will stop processing data and will be fully removed from the environment, including all associated data from Risk Management.'
        ),
        [IntegrationType.INTUNE]: this.dependencies.translateFn(
          'The sensor will no longer process data and it will be removed from the Sensor Management list.'
        ),
        [IntegrationType.GOOGLE_WS]: this.dependencies.translateFn(
          'The sensor will no longer process data and it will be removed from the Sensor Management list.'
        ),
        [IntegrationType.GCP]: this.dependencies.translateFn(
          'The sensor will no longer process data and it will be removed from the Sensor Management list.'
        ),
        [IntegrationType.MOBILE_SECURITY]: this.dependencies.translateFn(
          'The sensor will no longer process data and it will be removed from the Sensor Management list.'
        ),
        [IntegrationType.CLOUD_SECURITY]: this.dependencies.translateFn(
          'The sensor will no longer process data and it will be removed from the Sensor Management list.'
        ),
        [IntegrationType.ATLASSIAN_CLOUD]: this.dependencies.translateFn(
          'The sensor will no longer process data and it will be removed from the Sensor Management list.'
        ),
      },
      deleteDialogTextWarning: {
        [IntegrationType.GCP]: this.dependencies.translateFn(
          'Warning: Deleting the sensor will not deactivate your paid {PubSub} subscription.',
          {
            PubSub:
              '<a href="https://cloud.google.com/pubsub" target="_blank">' +
              this.dependencies.translateFn('Pub/Sub') +
              '</a>',
          }
        ),
      },
      deleteDialogCancelButtonLabel: this.dependencies.translateFn('Cancel'),
      deleteDialogConfirmButtonLabel: this.dependencies.translateFn('Delete'),
      editActionButtonLabel: this.dependencies.translateFn('Edit'),
      technicalSupportRequestButtonLabel: this.dependencies.translateFn('Open support ticket'),
      cannotAddIntegrationTooltip: this.dependencies.translateFn(
        'You need to have a valid product license with EDR before adding a new sensor.'
      ),
      errorTextsVulnerabilityAppliance: {
        [NSVAVirtualApplianceErrorReasons.SCAN_INTERFACES_CONFIG_NEEDED]: this.dependencies.translateFn(
          'Scan interfaces configuration needed.'
        ),
        [NSVAVirtualApplianceErrorReasons.SCAN_TARGETS_CONFIG_NEEDED]: this.dependencies.translateFn(
          'Scan targets configuration needed.'
        ),
        [NSVAVirtualApplianceErrorReasons.SCAN_TARGETS_INTERFACES_CONFIG_NEEDED]: this.dependencies.translateFn(
          'Scan targets and interfaces configuration needed.'
        ),
        [NSVAVirtualApplianceErrorReasons.GHOSTR_NO_BRAIN]: this.dependencies.translateFn(
          'Network Vulnerability module error. Contact Bitdefender Enterprise Support.'
        ),
        [NSVAVirtualApplianceErrorReasons.GHOSTR_NO_PCAP_DEVICE_SET]: this.dependencies.translateFn(
          'Network interface for promiscuous mode is not configured.'
        ),
        [NSVAVirtualApplianceErrorReasons.OS_UPGRADE_REQUIRED]: this.dependencies.translateFn(
          'The operating system must be upgraded. Go to the Network section and run an Update Security Server task on the VM.'
        ),
        [NSVAVirtualApplianceErrorReasons.CRITICAL_PROTECTION_DISABLED]: this.dependencies.translateFn(
          'The Bitdefender Global Protective Network (GPN) must be enabled in General > Agent > Settings > Options of the NSVA policy to maintain protection.'
        ),
        [NSVAVirtualApplianceErrorReasons.ACTIVE]: this.dependencies.translateFn('Active'),
        [NSVAVirtualApplianceErrorReasons.DISABLED]: this.dependencies.translateFn('Disabled'),
        [NSVAVirtualApplianceErrorReasons.UNLICENSED]: this.dependencies.translateFn('Unlicensed'),
      },
    };
  }

  public getTranslatedRisksSections(section: RisksSectionsStrings) {
    const risksSectionStrings = {
      [RisksSectionsStrings.cloudSecuritySection]: {
        title: this.dependencies.translateFn('Top cloud risks'),
        noDataAvailable: this.dependencies.translateFn('No data available for Top cloud risks'),
      },
      [RisksSectionsStrings.eraSection]: {
        title: this.dependencies.translateFn('Top endpoint risks'),
        noDataAvailable: this.dependencies.translateFn('No data available for Top endpoint risks'),
      },
      [RisksSectionsStrings.rootCause]: {
        title: this.dependencies.translateFn('Root cause risks'),
        noDataAvailable: this.dependencies.translateFn('No data available for Root cause risks'),
      },
    };
    return typeof risksSectionStrings[section] !== 'undefined'
      ? risksSectionStrings[section]
      : { title: this.dependencies.translateFn('Unknown'), noDataAvailable: this.dependencies.translateFn('Unknown') };
  }

  public getTranslatedIntegrationNameByType(integrationType: IntegrationType): string {
    if (!this.translations.integrationNameByType) {
      this.translations.integrationNameByType = {
        [IntegrationType.O365]: () => this.dependencies.translateFn('Office 365'),
        [IntegrationType.AD_ONPREM]: () => this.dependencies.translateFn('Active Directory'),
        [IntegrationType.AWS]: () => this.dependencies.translateFn('AWS'),
        [IntegrationType.AZURE_CW]: () => this.dependencies.translateFn('Azure Cloud'),
        [IntegrationType.AZURE_AD]: () => this.dependencies.translateFn('Azure AD'),
        [IntegrationType.NSVA]: () => this.dependencies.translateFn('Network Sensor'),
        [IntegrationType.INTUNE]: () => this.dependencies.translateFn('Microsoft Intune'),
        [IntegrationType.GOOGLE_WS]: () => this.dependencies.translateFn('Google Workspace'),
        [IntegrationType.GCP]: () => this.dependencies.translateFn('Google Cloud Platform'),
        [IntegrationType.MOBILE_SECURITY]: () => this.dependencies.translateFn('Security for Mobile'),
        [IntegrationType.CLOUD_SECURITY]: () => this.dependencies.translateFn('CSPM+'),
        [IntegrationType.ATLASSIAN_CLOUD]: () => this.dependencies.translateFn('Atlassian Cloud'),
      };
    }

    return typeof this.translations.integrationNameByType[integrationType] !== 'undefined'
      ? this.translations.integrationNameByType[integrationType]()
      : this.dependencies.translateFn('Unknown');
  }

  initialSetup() {
    this.dependenciesInjected = false;
    this.dependencies = {};
  }

  injectDependencies(dependencies: any) {
    this.dependencies = dependencies;
    this.dependenciesInjected = true;
  }

  public getEndpointDetailsString() {
    return {
      title: this.dependencies.translateFn('Endpoint Details'),
      name: this.dependencies.translateFn('Name'),
      ip: this.dependencies.translateFn('IP'),
      os: this.dependencies.translateFn('OS'),
      label: this.dependencies.translateFn('Label'),
      errorMessage: this.dependencies.translateFn('Unable to access the endpoint.'),
    };
  }

  public getRemoteShellTranslations() {
    return {
      remoteShellTitle: this.dependencies.translateFn('Remote Shell Connection'),
      remoteShellConnectionInfo: this.dependencies.translateFn('Remote shell connection info'),
      endSessionButtonText: this.dependencies.translateFn('End session'),
      endSessionDialog: {
        headline: this.dependencies.translateFn('End remote session?'),
        body: this.dependencies.translateFn(
          'This will terminate the current remote session. You can view all the session details in <b>User Activity</b>.'
        ),
        confirmButtonText: this.dependencies.translateFn('End session'),
        cancelButtonText: this.dependencies.translateFn('Cancel'),
      },
      inactivityTimeoutText: this.dependencies.translateFn('Session terminated due to inactivity.'),
      upload: {
        uploadInProgress: translateFn('Uploading selected file(s)...'),
        button: translateFn('Upload'),
        uploadFailed: translateFn('File upload processing error. Please try again.'),
      },
      download: {
        button: translateFn('Download'),
      },
    };
  }

  public getUploadWindowTranslations() {
    return {
      title: translateFn('Select the files you want to upload to the remote endpoint.'),
      uploadPathLabel: translateFn('Destination file path'),
      inputPathPlaceholder: translateFn('Choose destination path'),
      selectFilesFieldLabel: translateFn('Select file to upload'),
      browseButton: translateFn('Browse'),
      filesTitle: translateFn('Total size'),
      filesNumberTitle: translateFn('Selected files'),
    };
  }

  public getDownloadWindowTranslations() {
    return {
      title: translateFn('Select the files you want to download from the remote endpoint.'),
      downloadPathLabel: translateFn('Download files from'),
      fileArchiveDetailsInformation: translateFn(
        "The archive is available for download in <b>Investigation Files Activity</b>. You have 24 hours to retrieve it. Afterwards, the archive is automatically removed from Bitdefender's temporary storage. Be aware that this download option consists of retrieving files from your environment, encrypting them, and sending them to Bitdefender for temporary storage."
      ),
      passwordProtectLabel: translateFn('Password protection'),
      passwordProtectToggle: translateFn('Password protect the file archive'),
      archivePasswordLabel: translateFn('Password'),
      repeatPasswordLabel: translateFn('Confirm password'),
      warningPassword: translateFn(
        'Keep this password safe. GravityZone does not store this password, so you cannot retrieve it later.'
      ),
      passwordsDoNotMatch: translateFn('Passwords do not match.'),
      archivePasswordDetails: translateFn(
        'The password must meet the minimum complexity requirements: 12 characters length, one digit, one upper case, one lower case and one special character.'
      ),
    };
  }

  public getTerminalTranslations() {
    return {
      startSessionTitle: this.dependencies.translateFn('Start Session'),
      endSessionTitle: this.dependencies.translateFn('End Session'),
      startSessionInfo: {
        firstLine: this.dependencies.translateFn(
          'Start remote shell connection to investigate and take actions directly on the endpoint.'
        ),
        secondLine: this.dependencies.translateFn(
          'To continue, enter the two factor authentication code provided by the Authentication app.'
        ),
      },
      redirectUserInfo: this.dependencies.translateFn(
        "GravityZone will redirect you to your company's login page. Enter your single sign-on credentials to start the session."
      ),
      authenticationCodeLabel: this.dependencies.translateFn('Authentication code'),
      inputPlaceholder: this.dependencies.translateFn('Enter code...'),
      startSessionButtonText: this.dependencies.translateFn('Start Session'),
      endSessionInfoText: this.dependencies.translateFn(
        'You can check out all the session details by downloading the activity log archive, or access them later in <b>User Activity</b>.'
      ),

      sessionStartTimeLabel: this.dependencies.translateFn('Session started'),
      sessionEndTimeLabel: this.dependencies.translateFn('Session ended'),

      restartSessionButtonText: this.dependencies.translateFn('Start new session'),
      downloadArchiveButtonText: this.dependencies.translateFn('Download audit log'),

      archiveNotAvailable: this.dependencies.translateFn('Generating archive log. Try again in a couple of moments.'),

      errors: {
        endpointNotFound: this.dependencies.translateFn('Unable to access the endpoint.'),
        unmanagedEndpoint: this.dependencies.translateFn('Unmanaged endpoint. Requires security agent installation.'),
        moduleNotInstalled: this.dependencies.translateFn(
          'Remote Shell must be enabled through Policy. Contact your GravityZone administrator for additional rights.'
        ),
        maNotEnabled: this.dependencies.translateFn('Communication with endpoint could not be established.'),
        twoFactorAuthenticationDisabled: this.dependencies.translateFn('2FA must be enabled from My account.'),
        notEnoughUserRights: this.dependencies.translateFn(
          'Additional rights required. Contact your GravityZone administrator.'
        ),
        remoteAccessFeatureNotAvailable: this.dependencies.translateFn(
          'Your company does not meet all the security criteria to start a remote shell session.'
        ),
        missingIDP: this.dependencies.translateFn('Your company does not have an IdP set up.'),
      },
      downloadErrors: {
        defaultInternalError: archiveErrorCode => {
          return this.dependencies.translateFn(
            'Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.',
            {
              ERROR_CODE: archiveErrorCode,
            }
          );
        },
        centralizedStorageErrors: {
          uploadCancelled: this.dependencies.translateFn('The operation was canceled. Please try again.'),
          maxSizeExceeded: maxFilesSize => {
            this.dependencies.translateFn('The overall download size cannot exceed {MAX_SIZE}', {
              MAX_SIZE: convertUnits(maxFilesSize),
            });
          },
        },
        singleFileErrors: {
          internalError: (fileName, archiveErrorCode) => {
            return this.dependencies.translateFn(
              '{FILE_NAME} could not be downloaded due to internal endpoint error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.',
              {
                FILE_NAME: fileName,
                ERROR_CODE: archiveErrorCode,
              }
            );
          },
          notProcessed: (fileName, archiveErrorCode) => {
            return this.dependencies.translateFn(
              '{FILE_NAME} could not be downloaded due to internal endpoint error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.',
              {
                FILE_NAME: fileName,
                ERROR_CODE: archiveErrorCode,
              }
            );
          },
          invalidName: fileName => {
            return this.dependencies.translateFn(
              '{FILE_NAME} could not be downloaded because the archive name contains unsupported characters.',
              {
                FILE_NAME: fileName,
              }
            );
          },
          badPathname: fileName => {
            return this.dependencies.translateFn(
              '{FILE_NAME} could not be downloaded because the file path is not valid, or the file path contains unsupported characters.',
              {
                FILE_NAME: fileName,
              }
            );
          },
          relativePath: fileName => {
            return this.dependencies.translateFn(
              '{FILE_NAME} could not be downloaded because the file path provided is relative. Please provide the full file path.',
              {
                FILE_NAME: fileName,
              }
            );
          },
          pathNotFound: fileName => {
            return this.dependencies.translateFn(
              '{FILE_NAME} could not be downloaded because the file path is not valid.',
              {
                FILE_NAME: fileName,
              }
            );
          },
          emptyFiles: fileName => {
            return this.dependencies.translateFn(
              '{FILE_NAME} could not be downloaded because the file or folder does not contain any data.',
              {
                FILE_NAME: fileName,
              }
            );
          },
          accessDenied: fileName => {
            return this.dependencies.translateFn(
              '{FILE_NAME} could not be downloaded due to insufficient permissions. Choose a different folder.',
              {
                FILE_NAME: fileName,
              }
            );
          },
          incompletePacking: fileName => {
            return this.dependencies.translateFn(
              'Some files could not be downloaded due to processing errors. Please try again.',
              {
                FILE_NAME: fileName,
              }
            );
          },
          packingFailed: fileName => {
            return this.dependencies.translateFn(
              '{FILE_NAME} could not be downloaded due to processing errors. Please try again.',
              {
                FILE_NAME: fileName,
              }
            );
          },
        },
        multipleFilesErrors: {
          internalError: (numberOfFiles, archiveErrorCode) => {
            return this.dependencies.translateFn(
              '{NUMBER_OF_FILES} files could not be downloaded due to internal endpoint error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.',
              {
                NUMBER_OF_FILES: numberOfFiles,
                ERROR_CODE: archiveErrorCode,
              }
            );
          },
          notProcessed: (numberOfFiles, archiveErrorCode) => {
            return this.dependencies.translateFn(
              '{NUMBER_OF_FILES} files could not be downloaded due to internal endpoint error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.',
              {
                NUMBER_OF_FILES: numberOfFiles,
                ERROR_CODE: archiveErrorCode,
              }
            );
          },
          invalidName: numberOfFiles => {
            return this.dependencies.translateFn(
              '{NUMBER_OF_FILES} files could not be downloaded because the archive name contains unsupported characters.',
              {
                NUMBER_OF_FILES: numberOfFiles,
              }
            );
          },
          badPathname: numberOfFiles => {
            return this.dependencies.translateFn(
              '{NUMBER_OF_FILES} files could not be downloaded because the file path is not valid, or the file path contains unsupported characters.',
              {
                NUMBER_OF_FILES: numberOfFiles,
              }
            );
          },
          relativePath: numberOfFiles => {
            return this.dependencies.translateFn(
              '{NUMBER_OF_FILES} files could not be downloaded because the file path provided is relative. Please provide the full file path.',
              {
                NUMBER_OF_FILES: numberOfFiles,
              }
            );
          },
          pathNotFound: numberOfFiles => {
            return this.dependencies.translateFn(
              '{NUMBER_OF_FILES} files could not be downloaded because the file path is not valid.',
              {
                NUMBER_OF_FILES: numberOfFiles,
              }
            );
          },
          emptyFiles: numberOfFiles => {
            return this.dependencies.translateFn(
              '{NUMBER_OF_FILES} files could not be downloaded because the file or folder does not contain any data.',
              {
                NUMBER_OF_FILES: numberOfFiles,
              }
            );
          },
          accessDenied: numberOfFiles => {
            return this.dependencies.translateFn(
              '{NUMBER_OF_FILES} files could not be downloaded due to insufficient permissions. Choose a different folder.',
              {
                NUMBER_OF_FILES: numberOfFiles,
              }
            );
          },
          incompletePacking: numberOfFiles => {
            return this.dependencies.translateFn(
              '{NUMBER_OF_FILES} files could not be downloaded due to processing errors. Please try again.',
              {
                NUMBER_OF_FILES: numberOfFiles,
              }
            );
          },
          packingFailed: numberOfFiles => {
            return this.dependencies.translateFn(
              '{NUMBER_OF_FILES} files could not be downloaded due to processing errors. Please try again.',
              {
                NUMBER_OF_FILES: numberOfFiles,
              }
            );
          },
        },
      },
    };
  }

  public getMdrTranslations() {
    return {
      mdrServiceName: 'Managed Detection and Response Foundations',
      resellingMdr: 'Managed Detection and Response Foundations',
      mdrInfoText: this.dependencies.translateFn(
        '{MDR_SERVICE_NAME} is a modern, out-of-the-box MDR service, for Managed Service Providers (MSPs) and businesses of all sizes, that provides 24x7 security monitoring, a $0 incident response retainer and intel-based threat hunting performed by a certified team of security experts. {MDR_ACRONYM} requires {EDR_NAME} and {ATS_NAME} to be enabled in GravityZone.',
        {
          MDR_SERVICE_NAME: 'Bitdefender Managed Detection and Response Foundations',
          MDR_ACRONYM: 'MDR',
          EDR_NAME: 'Endpoint Detection and Response (EDR)',
          ATS_NAME: 'Advanced Threat Protection (ATS)',
        }
      ),
      learnMoreMdr: `<span>${htmlEncode(
        this.dependencies.translateFn('Learn more about the service requirements and activation.')
      )}</span>`,
    };
  }

  public getTranslatedMdrFlavor(flavor: MdrServiceFlavor): string {
    if (!this.translations.mdrServiceFlavor) {
      this.translations.mdrServiceFlavor = {
        [MdrServiceFlavor.FOUNDATIONS]: () => 'MDR Foundations for MSP',
        [MdrServiceFlavor.FOUNDATIONS_LEGACY]: () => 'MDR Foundations',
        [MdrServiceFlavor.PREMIUM]: () => 'MDR Premium',
        [MdrServiceFlavor.ENTERPRISE]: () => 'MDR Enterprise',
        [MdrServiceFlavor.BITDEFENDER_MDR]: () => 'Bitdefender MDR',
        [MdrServiceFlavor.BITDEFENDER_MDR_PLUS]: () => 'Bitdefender MDR Plus',
      };
    }

    return this.translations.mdrServiceFlavor[flavor]();
  }

  public getTranslatedProductServiceStatuses(status: ProductServiceStatus): string {
    if (!this.translations.productServiceStatuses) {
      this.translations.productServiceStatuses = {
        [ProductServiceStatus.ACTIVE]: () => this.dependencies.translateFn('Active'),
        [ProductServiceStatus.INACTIVE]: () => this.dependencies.translateFn('Inactive'),
        [ProductServiceStatus.ONBOARDING]: () => this.dependencies.translateFn('Onboarding'),
        [ProductServiceStatus.SWITCHING]: () => this.dependencies.translateFn('Switching flavor'),
        [ProductServiceStatus.DISABLING]: () => this.dependencies.translateFn('Disabling'),
        [ProductServiceStatus.ENROLLING]: () => this.dependencies.translateFn('Enrolling'),
      };
    }

    return this.translations.productServiceStatuses[status]();
  }

  public getTranslatedMetadataStatus(status: MetadataStatus): string {
    if (!this.translations.metadataStatuses) {
      this.translations.metadataStatuses = {
        [MetadataStatus.PENDING]: () => this.dependencies.translateFn('Pending'),
        [MetadataStatus.DELIVERED]: () => this.dependencies.translateFn('Successful'),
        [MetadataStatus.DISMISSED]: () => this.dependencies.translateFn('Timed out'),
        [MetadataStatus.DELIVERED_WITH_ERROR]: () => this.dependencies.translateFn('Error'),
        [MetadataStatus.NOT_DELIVERED]: () => this.dependencies.translateFn('Timed out'),
        [MetadataStatus.FAILED_SESSION_START]: () => this.dependencies.translateFn('Failed to connect'),
      };
    }
    return this.translations.metadataStatuses[status]();
  }

  public getTranslatedNamesInstallationPackages(
    packageName: PackageNameMappings,
    packageCompanyName: string,
    hasManageCompanies: boolean
  ): string {
    if (
      hasManageCompanies &&
      (Object.values(PackageNameMappingsHasManageCompanies) as PackageNameMappingsHasManageCompanies[]).includes(
        packageName as unknown as PackageNameMappingsHasManageCompanies
      )
    ) {
      if (!this.translations.dynamicInstallationPackageNames) {
        const fratTranslator = (companyName: string) =>
          this.dependencies.translateFn('{companyName} - (Free Risk Assessment Tool)', {
            companyName: companyName,
          });

        const fratRelayTranslator = (companyName: string) =>
          this.dependencies.translateFn('{companyName} - (Free Risk Assessment Tool with Relay)', {
            companyName: companyName,
          });

        this.translations.dynamicInstallationPackageNames = {
          [PackageNameMappingsHasManageCompanies.FREE_RISK_ASSESSMENT_TOOL_PACKAGE]: fratTranslator,
          [PackageNameMappingsHasManageCompanies.FREE_RISK_ASSESSMENT_TOOLS_PACKAGE]: fratTranslator,
          [PackageNameMappingsHasManageCompanies.FREE_RISK_ASSESSMENT_TOOL_WITH_RELAY_PACKAGE]: fratRelayTranslator,
          [PackageNameMappingsHasManageCompanies.FREE_RISK_ASSESSMENT_TOOLS_WITH_RELAY_PACKAGE]: fratRelayTranslator,
        };
      }
      return this.translations.dynamicInstallationPackageNames[packageName](packageCompanyName);
    }

    if (!this.translations.installationPackagesNames) {
      this.translations.installationPackagesNames = {
        [PackageNameMappings.DEFAULT_UPDATE_SERVER_PACKAGE]: this.dependencies.translateFn(
          'Default Update Server Package'
        ),
        [PackageNameMappings.DEFAULT_SECURITY_SERVER_PACKAGE]: this.dependencies.translateFn(
          'Security Server Virtual Appliance'
        ),
        [PackageNameMappings.DEFAULT_BDTOOLS_PACKAGE]: this.dependencies.translateFn(
          'Default Bitdefender Tools Package'
        ),
        [PackageNameMappings.DEFAULT_ENDPOINT_SECURITY_PACKAGE]: this.dependencies.translateFn(
          'Default Endpoint Security Package'
        ),
        [PackageNameMappings.DEFAULT_SANDBOX_ANALYZER_PACKAGE]: this.dependencies.translateFn('Sandbox Analyzer'),
        [PackageNameMappings.DEFAULT_NETWORK_SECURITY_VIRTUAL_APPLIANCE_PACKAGE]: this.dependencies.translateFn(
          'Network Security Virtual Appliance'
        ),
        [PackageNameMappings.DEFAULT_NETWORK_SENSOR_VIRTUAL_APPLIANCE]: this.dependencies.translateFn(
          'Network Sensor Virtual Appliance'
        ),
        [PackageNameMappings.DEFAULT_K8S_PACKAGE]: this.dependencies.translateFn('Kubernetes Virtual Appliance'),
        [PackageNameMappings.FREE_RISK_ASSESSMENT_TOOL_PACKAGE]: translateFn('Free Risk Assessment Tool'),
        [PackageNameMappings.FREE_RISK_ASSESSMENT_TOOLS_PACKAGE]: translateFn('Free Risk Assessment Tool'),
        [PackageNameMappings.FREE_RISK_ASSESSMENT_TOOL_WITH_RELAY_PACKAGE]: translateFn(
          'Free Risk Assessment Tool with Relay'
        ),
        [PackageNameMappings.FREE_RISK_ASSESSMENT_TOOLS_WITH_RELAY_PACKAGE]: translateFn(
          'Free Risk Assessment Tool with Relay'
        ),
      };
    }

    return this.translations.installationPackagesNames[packageName];
  }

  public getNirvanaErrorCodesTranslations() {
    return {
      [NirvanaErrorCode.UNKNOWN]: this.dependencies.translateFn('An internal server error has occurred'),
      [NirvanaErrorCode.STREAM_START_FAILED]: this.dependencies.translateFn(
        'Communication with endpoint could not be established. Try again later. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.STREAM_CLOSED]: this.dependencies.translateFn(
        'Connection error. Try again later. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.STREAM_ALREADY_OPEN]: this.dependencies.translateFn(
        'Connection error. Try again later. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.STREAM_ID_INVALID]: this.dependencies.translateFn(
        'Connection error. Try again later. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.STREAM_NOT_READY]: this.dependencies.translateFn(
        'Connection error. Try again later. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.CLIENT_RESPONSE_INVALID]: this.dependencies.translateFn(
        'Connection error. Try again later. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.INVALID_MESSAGE_TYPE]: this.dependencies.translateFn(
        'Connection error. Try again later. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.INVALID_MESSAGE]: this.dependencies.translateFn(
        'Connection error. Try again later. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.INVALID_DATA]: this.dependencies.translateFn(
        'Connection error. Try again later. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.ACCESS_DENIED]: this.dependencies.translateFn(
        'Unauthorized connection. Reopen the Remote Session browser tab. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.AUTHENTICATION_FAILED]: this.dependencies.translateFn(
        'Authentication failed. Try again later. If the problem persists, contact the Bitdefender Customer Support team.'
      ),
      [NirvanaErrorCode.INIT_TIMEOUT]: this.dependencies.translateFn(
        'Connection timeout. Try restarting the target endpoint.'
      ),
      [NirvanaErrorCode.CLIENT_DISCONNECTED]: this.dependencies.translateFn(
        'You were disconnected. Try starting a new session.'
      ),
      [NirvanaErrorCode.USER_VALIDATION_ERROR]: this.dependencies.translateFn('User does not meet all requirements.'),
      [NirvanaErrorCode.ENDPOINT_VALIDATION_ERROR]: this.dependencies.translateFn(
        'Endpoint does not meet all requirements.'
      ),
      [NirvanaErrorCode.TWO_FACTOR_AUTHENTICATION_FAILED]: this.dependencies.translateFn(
        'Authentication code is not valid.'
      ),
      [NirvanaErrorCode.SAME_USER_SAME_ENDPOINT_LIMIT_REACHED]: this.dependencies.translateFn(
        'A remote shell session is already in progress.'
      ),
      [NirvanaErrorCode.SAME_USER_MULTIPLE_ENDPOINTS_LIMIT_REACHED]: this.dependencies.translateFn(
        'You have exceeded the threshold of running remote shell sessions.'
      ),
      [NirvanaErrorCode.MULTIPLE_USERS_SAME_ENDPOINT_LIMIT_REACHED]: this.dependencies.translateFn(
        'Too many users are currently connected to this endpoint.'
      ),
      [NirvanaErrorCode.COMPANY_VALIDATION_ERROR]: this.dependencies.translateFn(
        'Company does not meet all requirements.'
      ),
    };
  }

  public translateNirvanaErrorCode(errorCode: NirvanaErrorCode): string {
    const errorCodesTranslations = this.getNirvanaErrorCodesTranslations();

    return errorCodesTranslations[errorCode]
      ? errorCodesTranslations[errorCode]
      : errorCodesTranslations[NirvanaErrorCode.UNKNOWN];
  }

  /**
   * @param {number} integrationType
   */
  public translateAddSensorTooltipMessage(integrationType: number): string {
    const addSensorTooltipMessage = {
      [IntegrationType.ATLASSIAN_CLOUD]: this.dependencies.translateFn(
        'Clear the unsaved input fields before adding a sensor.'
      ),
    };
    return addSensorTooltipMessage[integrationType] ? addSensorTooltipMessage[integrationType] : '';
  }

  /**
   * @param {number} errorCode
   */
  public translateSensorConnectivityErrorCodes(errorCode: number): string {
    const errorCodesTranslations = {
      [SensorConnectivityErrors.AUTHENTICATION_ERROR]: this.dependencies.translateFn(
        'Authentication failed. The provided credentials are incorrect.'
      ),
      [SensorConnectivityErrors.PERMISSION_ERROR]: this.dependencies.translateFn(
        'Check the requirements and try again.'
      ),
      [SensorConnectivityErrors.NON_EXISTENT_QUEUE]: this.dependencies.translateFn(
        'Connection failed. The SQS link is incorrect.'
      ),
      [SensorConnectivityErrors.INVALID_CLIENT_TOKEN_ID]: this.dependencies.translateFn(
        'Connection failed. The Access key ID or Secret access key is incorrect.'
      ),
      [SensorConnectivityErrors.SIGNATURE_DOES_NOT_MATCH]: this.dependencies.translateFn(
        'Connection failed. The Secret access key is incorrect.'
      ),
      [SensorConnectivityErrors.REQUIRED_GROUP_POLICY]: this.dependencies.translateFn('Group policy required.'),
      [SensorConnectivityErrors.REQUIRED_CA_POLICY]: this.dependencies.translateFn('CA policy required.'),
      [SensorConnectivityErrors.BEST_NOT_INSTALLED]: this.dependencies.translateFn(
        'BEST with EDR is not installed or enabled. Make the necessary changes and try again. If the problem persists, contact the Bitdefender Enterprise Support team.'
      ),
      [SensorConnectivityErrors.NO_CONNECTION_TO_BEST]: this.dependencies.translateFn('No connection.'),
      [SensorConnectivityErrors.RESTART_REQUIRED]: this.dependencies.translateFn('Restart required.'),
      [SensorConnectivityErrors.LICENSE_INACTIVE]: this.dependencies.translateFn(
        'You need to renew your GravityZone or sensor license key.'
      ),
      [SensorConnectivityErrors.LURKER_CONNECTION_ERROR]: this.dependencies.translateFn(
        'Unable to connect to the GravityZone Incidents Server. For more information, contact Bitdefender Technical Support.'
      ),
      [SensorConnectivityErrors.GHOSTR_NO_PROBE]: this.dependencies.translateFn(
        'Traffic parsing error. Contact Bitdefender Enterprise Support.'
      ),
      [SensorConnectivityErrors.GHOSTR_CANNOT_OPEN_PCAP_INTERFACE]: this.dependencies.translateFn(
        'Connection failed. Make sure the network interface is configured in promiscuous mode.'
      ),
      [SensorConnectivityErrors.GHOSTR_NO_TRAFFIC]: this.dependencies.translateFn(
        'The sensor has detected no network traffic on this network.'
      ),
      [SensorConnectivityErrors.SUBNET_CONFIG_NEEDED]: this.dependencies.translateFn(
        'No networks defined. Please follow the configuration guide and define at least one network to be monitored.'
      ),
      [SensorConnectivityErrors.GATEWAY_CONFIG_NEEDED]: this.dependencies.translateFn(
        'No routers defined. Please follow the configuration guide and define the routers for your network.'
      ),
      [SensorConnectivityErrors.SUBNET_GATEWAY_CONFIG_NEEDED]: this.dependencies.translateFn(
        'No networks or routers defined. Please follow the configuration guide to configure the Network Sensor Virtual Appliance.'
      ),
      [SensorConnectivityErrors.GHOSTR_NO_BRAIN]: this.dependencies.translateFn(
        'Network Vulnerability module error. Contact Bitdefender Enterprise Support.'
      ),
      [SensorConnectivityErrors.GHOSTR_NO_PCAP_DEVICE_SET]: this.dependencies.translateFn(
        'Network interface for promiscuous mode is not configured.'
      ),
      [SensorConnectivityErrors.CRITICAL_PROTECTION_DISABLED]: this.dependencies.translateFn(
        'The Bitdefender Global Protective Network (GPN) must be enabled in General > Agent > Settings > Options of the NSVA policy to maintain protection.'
      ),
      [SensorConnectivityErrors.OS_UPGRADE_REQUIRED]: this.dependencies.translateFn(
        'The operating system must be upgraded. Go to the Network section and run an Update Security Server task on the VM.'
      ),
      [SensorConnectivityErrors.UNKNOWN_ERROR]: this.dependencies.translateFn('Something went wrong...'),
    };

    return errorCodesTranslations[errorCode] ? errorCodesTranslations[errorCode] : '';
  }

  public getSmartViewsTranslations() {
    return {
      panelTitle: this.dependencies.translateFn('Smart Views'),
      emptySavedViewsText: this.dependencies.translateFn('No saved views yet'),
      emptyFavoritesViewsText: this.dependencies.translateFn('No favorite views yet'),
      emptyRecentViewsText: this.dependencies.translateFn('No recent views yet'),
      emptyDefaultViewsText: this.dependencies.translateFn('No default views yet'),
      genericPanelTitle: this.dependencies.translateFn('Smart views'),
      actions: {
        save: this.dependencies.translateFn('Save#preserve state'),
        delete: this.dependencies.translateFn('Delete'),
        cancel: this.dependencies.translateFn('Cancel'),
        saveAs: this.dependencies.translateFn('Save as'),
        discard: this.dependencies.translateFn('Discard changes'),
        favorite: this.dependencies.translateFn('Favorite'),
      },
      saveViewFormDialog: {
        checkboxLabel: this.dependencies.translateFn('Share within organisation'),
        dialogText: this.dependencies.translateFn(
          'Do you want to save the current view? You will find the new entry in the CUSTOM section.'
        ),
        nameFieldLabel: this.dependencies.translateFn('Name'),
        nameInputErrorText: this.dependencies.translateFn('This field is required'),
        nameInputPlaceholder: this.dependencies.translateFn('Type here'),
        headline: this.dependencies.translateFn('Save view'),
      },
      saveGridViewFormDialog: {
        dialogText: this.dependencies.translateFn(
          'The current view (table entries, column visibility, order, and size) will be available in the smart views panel.'
        ),
      },
      deleteViewFormDialog: {
        title: this.dependencies.translateFn('Delete view'),
      },
    };
  }

  public getKillChainPhasesTranslations(killChainPhase) {
    const killChainPhases = {
      [KILL_CHAIN_PHASES.INITIAL_ACCESS]: this.dependencies.translateFn('Initial Access'),
      [KILL_CHAIN_PHASES.EXECUTION]: this.dependencies.translateFn('Execution'),
      [KILL_CHAIN_PHASES.PERSISTENCE]: this.dependencies.translateFn('Persistence'),
      [KILL_CHAIN_PHASES.PRIVILEGE_ESCALATION]: this.dependencies.translateFn('Privilege Escalation'),
      [KILL_CHAIN_PHASES.DEFENCE_EVASION]: this.dependencies.translateFn('Defense Evasion'),
      [KILL_CHAIN_PHASES.CREDENTIAL_ACCESS]: this.dependencies.translateFn('Credential Access'),
      [KILL_CHAIN_PHASES.DISCOVERY]: this.dependencies.translateFn('Discovery'),
      [KILL_CHAIN_PHASES.LATERAL_MOVEMENT]: this.dependencies.translateFn('Lateral Movement'),
      [KILL_CHAIN_PHASES.COLLECTION]: this.dependencies.translateFn('Collection'),
      [KILL_CHAIN_PHASES.COMMAND_AND_CONTROL]: this.dependencies.translateFn('Command and Control'),
      [KILL_CHAIN_PHASES.EXFILTRATION]: this.dependencies.translateFn('Exfiltration'),
      [KILL_CHAIN_PHASES.IMPACT]: this.dependencies.translateFn('Impact'),
    };

    return killChainPhases[killChainPhase] ? killChainPhases[killChainPhase] : null;
  }

  public getLiveSearchGridStrings(): any {
    return {
      notStartedMessage: this.dependencies.translateFn('Get started with your investigation'),
      notStartedAdditionalText: this.dependencies.translateFn(
        'Select one of the available predefined queries or type in your own and click Run Query.'
      ),
      noResultsMessage: this.dependencies.translateFn('No results found.'),
      inProgressMessage: this.dependencies.translateFn('Query in progress, no results found so far'),
      resultsExpired: this.dependencies.translateFn('Results have expired.'),
      reRunMessage: this.dependencies.translateFn('To get new results, click Run Query.'),
    };
  }

  public getMetadataGridStrings(): object {
    return {
      assignTagsLabel: this.dependencies.translateFn('Assign Tags'),
      notStartedMessage: this.dependencies.translateFn('Get started with your investigation'),
      notStartedAdditionalText: this.dependencies.translateFn(
        'Select one of the available predefined queries or type in your own and click Run Query.'
      ),
      noResultsMessage: this.dependencies.translateFn('No metadata results found'),
      inProgressMessage: this.dependencies.translateFn('Query in progress, no metadata results found so far'),
      naMessage: this.dependencies.translateFn('N/A'),
      downloadMetadata: this.dependencies.translateFn('Download as CSV'),
    };
  }

  public getIncidentsDetailsSidePanelString(): any {
    return {
      incidentDetailsTitle: this.dependencies.translateFn('Incident details'),
      graphButtonTextForEdrIncidentPanel: this.dependencies.translateFn('View graph'),
      eventsButtonTextForEdrIncidentPanel: this.dependencies.translateFn('View events'),
      viewIncidentButtonForXdrIncidentPanel: this.dependencies.translateFn('View incident'),
      viewResponsesButtonForXdrIncidentPanel: this.dependencies.translateFn('View responses'),
      noResults: this.dependencies.translateFn('N/A'),

      analysisSectionTitle: this.dependencies.translateFn('Incident analysis'),
      status: this.dependencies.translateFn('Status'),
      assignee: this.dependencies.translateFn('Assignee'),
      priority: this.dependencies.translateFn('Priority'),

      generalSectionTitle: this.dependencies.translateFn('General'),
      id: this.dependencies.translateFn('ID'),
      created: this.dependencies.translateFn('Created on'),
      lastUpdated: this.dependencies.translateFn('Last updated on'),
      company: this.dependencies.translateFn('Company'),
      severity: this.dependencies.translateFn('Severity'),

      detectionsSectionTitle: this.dependencies.translateFn('Detections'),
      incidentTrigger: this.dependencies.translateFn('Incident trigger'),

      techniquesSectionTitle: this.dependencies.translateFn('Att&ck techniques'),

      sectionTitle: this.dependencies.translateFn('Attack info'),
      actionTaken: this.dependencies.translateFn('Action taken'),
      alerts: this.dependencies.translateFn('Alerts'),
      entities: this.dependencies.translateFn('Entities'),
      resources: this.dependencies.translateFn('Resources'),
      correlatedIncidents: this.dependencies.translateFn('Correlated incidents'),
      killChainPhases: this.dependencies.translateFn('Kill chain phases'),
      actorsTitle: this.dependencies.translateFn('Actors'),
      showMoreBtn: this.dependencies.translateFn('Show more'),
      singleEntityLabel: this.dependencies.translateFn('Entity'),
      operatingSystem: this.dependencies.translateFn('Endpoint OS'),
      artifacts: this.dependencies.translateFn('Artifacts'),
    };
  }

  public getPhasrCategoryString(phasrCategory: DetectedIncidentsTemplatesStringKeys): string {
    const phasrCategories = {
      [DetectedIncidentsTemplatesStringKeys.HACKTOOLS]: this.dependencies.translateFn('Piracy tools'),
      [DetectedIncidentsTemplatesStringKeys.LIVING_OFF_THE_LAND_BINARIES]:
        this.dependencies.translateFn('Living off the land binaries'),
      [DetectedIncidentsTemplatesStringKeys.MINERS]: this.dependencies.translateFn('Crypto miners'),
      [DetectedIncidentsTemplatesStringKeys.REMOTE_ADMIN_TOOLS]: this.dependencies.translateFn('Remote admin tools'),
      [DetectedIncidentsTemplatesStringKeys.TAMPERING_TOOLS]: this.dependencies.translateFn('Tampering tools'),
    };

    return phasrCategories[phasrCategory];
  }

  public getTargetsStrings(): any {
    return {
      defaultSelectTargetsTitle: this.dependencies.translateFn('Select Targets'),
      defaultSelectedTargetsTitle: this.dependencies.translateFn('Selected Groups'),
      noTargetsSelectedError: this.dependencies.translateFn('You must select at least one target'),
      searchPlaceholder: this.dependencies.translateFn('Search'),
    };
  }

  public getScanTypes(scanType: ScanTypes) {
    const scanTypesTranslations = {
      [ScanTypes.PrivateCloud]: translateFn('Central Scan'),
      [ScanTypes.Cloud]: translateFn('Hybrid Scan'),
      [ScanTypes.Local]: translateFn('Local Scan'),
    };

    return scanTypesTranslations[scanType];
  }

  public translateEdrResponseActionsType(type: ResponseActionsType): string {
    if (!this.translations.edrResponseActionsTypes) {
      this.translations.edrResponseActionsTypes = {
        [ResponseActionsType.CONTAINMENT]: translateFn('Containment'),
        [ResponseActionsType.MITIGATION]: translateFn('Mitigation'),
        [ResponseActionsType.HARDENING]: translateFn('Hardening'),
        [ResponseActionsType.INVESTIGATION]: translateFn('Investigation'),
      };
    }

    return this.translations.edrResponseActionsTypes[type];
  }

  public translateEdrResponseActionsAction(action: ResponseActionsAction): string {
    if (!this.translations.edrResponseActionsActions) {
      this.translations.edrResponseActionsActions = {
        [ResponseActionsAction.ISOLATE_ENDPOINT]: translateFn('Isolate Endpoint'),
        [ResponseActionsAction.RESTORE_ENDPOINT]: translateFn('Restore Endpoint'),
        [ResponseActionsAction.PATCH_INSTALL]: translateFn('Patch install'),
        [ResponseActionsAction.ADD_TO_QUARANTINE]: translateFn('Quarantine'),
        [ResponseActionsAction.RESTORE_FROM_QUARANTINE]: translateFn('Restore from Quarantine'),
        [ResponseActionsAction.DELETE]: translateFn('Delete'),
        [ResponseActionsAction.DISINFECT]: translateFn('Disinfect'),
        [ResponseActionsAction.KILL_PROCESS]: translateFn('Kill Process'),
        [ResponseActionsAction.ADD_TO_SANDBOX]: translateFn('Add to Sandbox'),
        [ResponseActionsAction.COLLECT_INVESTIGATION_PACKAGE]: translateFn('Collect Investigation Package'),
        [ResponseActionsAction.SCAN]: translateFn('Antimalware scan'),
        [ResponseActionsAction.RISK_SCAN]: translateFn('Risk scan'),
      };
    }

    return this.translations.edrResponseActionsActions[action];
  }

  public translateEdrResponseActionsTarget(target: ResponseActionsTarget): string {
    if (!this.translations.edrResponseActionsTargets) {
      this.translations.edrResponseActionsTargets = {
        [ResponseActionsTarget.ENDPOINT]: translateFn('Endpoint'),
        [ResponseActionsTarget.FILE]: translateFn('File'),
        [ResponseActionsTarget.PROCESS]: translateFn('Process'),
        [ResponseActionsTarget.REGISTRY_KEY]: translateFn('Registry Key'),
        [ResponseActionsTarget.REGISTRY_VALUE]: translateFn('Registry Value'),
      };
    }

    return this.translations.edrResponseActionsTargets[target];
  }

  public translateXdrResponseStatus(status: XdrResponseStatus): string {
    if (!this.translations.xdrResponseActionsStatuses) {
      this.translations.xdrResponseActionsStatuses = {
        [XdrResponseStatus.TO_BE_EXECUTED]: translateFn('Action needed'),
        [XdrResponseStatus.PENDING]: translateFn('Pending'),
        [XdrResponseStatus.IN_EXECUTION]: translateFn('In progress'),
        [XdrResponseStatus.EXECUTION_SUCCESS]: translateFn('Successful'),
        [XdrResponseStatus.EXECUTION_FAILED]: translateFn('Failed'),
        [XdrResponseStatus.DONE_EXTERNALLY]: translateFn('Mark as done'),
        [XdrResponseStatus.DISMISSED]: translateFn('Dismissed'),
        [XdrResponseStatus.EXTERNAL_ACTION_NEEDED]: translateFn('External action needed'),
      };
    }

    return this.translations.xdrResponseActionsStatuses[status];
  }

  public translateEdrResponseActionsError(errorId: ResponseActionsErrorId, errorDescription: string | null): string {
    if (errorDescription) {
      if (ResponseActionsErrorId.TASK_ERROR === errorId) {
        return translateFn('Task error - {errorDescription}', { errorDescription });
      } else if (ResponseActionsErrorId.CUSTOM_RULE_ERROR === errorId) {
        return translateFn('Error at custom rule - {errorDescription}', { errorDescription });
      } else if (ResponseActionsErrorId.UNKNOWN === errorId) {
        return errorDescription;
      }
    }

    if (!this.translations.edrResponseActionsErrorIds) {
      this.translations.edrResponseActionsErrorIds = {
        [ResponseActionsErrorId.TIMEOUT]: translateFn('Timeout'),
        [ResponseActionsErrorId.TASK_ERROR]: translateFn('Task unknown error'),
        [ResponseActionsErrorId.TASK_TIMEOUT]: translateFn('Task timeout'),
        [ResponseActionsErrorId.ENDPOINT_PROTECTION_ERROR]: translateFn('Endpoint error'),
        [ResponseActionsErrorId.ENDPOINT_PROTECTION_NOT_PERMITTED]: translateFn('Not permitted by endpoint'),
        [ResponseActionsErrorId.ENDPOINT_PROTECTION_CORE]: translateFn('Endpoint core error'),
        [ResponseActionsErrorId.UNKNOWN]: translateFn('Unknown error'),
      };
    }

    return this.translations.edrResponseActionsErrorIds[errorId] as string;
  }

  public translateXdrResponseActionsError(errorCode: XdrResponseErrorId, originalError?: string): string {
    if (typeof this.translations.xdrResponseActionsErrorIds === 'undefined') {
      this.translations.xdrResponseActionsErrorIds = {
        [XdrResponseErrorId.AUTHENTICATION_ERROR]: translateFn('Authentication error'),
        [XdrResponseErrorId.PERMISSION_ERROR]: translateFn('Insufficient privileges to complete the operation'),
        [XdrResponseErrorId.ENDPOINT_NOT_FOUND]: translateFn('Endpoint not found'),
        [XdrResponseErrorId.PROTECTED_ENTITY_NOT_FOUND]: translateFn('Endpoint not found'),
        [XdrResponseErrorId.DOMAIN_NOT_FOUND]: translateFn('Domain not found'),
        [XdrResponseErrorId.RESPONSE_ACTIONS_DISABLED_FOR_SENSOR]: translateFn(
          'Response actions are disabled for this sensor'
        ),
        [XdrResponseErrorId.NO_DOMAIN]: translateFn('Domain not found'),
        [XdrResponseErrorId.DISABLED_SENSOR]: translateFn('Sensor is disabled'),
        [XdrResponseErrorId.UNFETCHABLE_SENSOR]: translateFn('Unable to fetch sensor data'),
        [XdrResponseErrorId.UNKNOWN_ERROR]: translateFn('Unknown error'),
      };
    }

    if (
      (!this.translations.xdrResponseActionsErrorIds[errorCode] || errorCode === XdrResponseErrorId.UNKNOWN_ERROR) &&
      originalError &&
      originalError.trim().length > 0
    ) {
      return originalError;
    }

    const errorMessage =
      this.translations.xdrResponseActionsErrorIds[errorCode] || translateFn('An unknown error occurred.');

    if (originalError && originalError.trim().length > 0) {
      return `${errorMessage}: ${originalError}`;
    }

    return errorMessage;
  }

  public translateEdrResponseActionsStatus(status: ResponseActionsStatus): string {
    if (!this.translations.edrResponseActionsStatuses) {
      this.translations.edrResponseActionsStatuses = {
        [ResponseActionsStatus.PENDING]: translateFn('Pending'),
        [ResponseActionsStatus.IN_EXECUTION]: translateFn('In progress'),
        [ResponseActionsStatus.EXECUTION_SUCCESS]: translateFn('Successful'),
        [ResponseActionsStatus.EXECUTION_FAILED]: translateFn('Failed'),
        [ResponseActionsStatus.REBOOT_NEEDED]: translateFn('Reboot needed'),
        [ResponseActionsStatus.ACTION_NEEDED]: translateFn('Action needed'),
        [ResponseActionsStatus.DISMISSED]: translateFn('Dismissed'),
        [ResponseActionsStatus.DONE_EXTERNALLY]: translateFn('Marked as done'),
      };
    }

    return this.translations.edrResponseActionsStatuses[status];
  }

  public translateEdrResponseActionsSource(source: ResponseActionsSource): string {
    if (!this.translations.edrResponseActionsSources) {
      this.translations.edrResponseActionsSources = {
        [ResponseActionsSource.MANUAL]: translateFn('Manual'),
        [ResponseActionsSource.CUSTOM_RULE]: translateFn('Custom Rule'),
        [ResponseActionsSource.ENDPOINT_PROTECTION]: translateFn('Endpoint Protection'),
      };
    }

    return this.translations.edrResponseActionsSources[source];
  }

  public translateCustomRulesAutomaticActionsType(type: CustomRulesAutomaticActionsType): string {
    if (!this.translations.customRulesAutomaticActionsTypes) {
      this.translations.customRulesAutomaticActionsTypes = {
        [CustomRulesAutomaticActionsType.ISOLATE]: translateFn('Isolate'),
        [CustomRulesAutomaticActionsType.COLLECT_INVESTIGATION_PACKAGE]: translateFn('Collect investigation package'),
        [CustomRulesAutomaticActionsType.ADD_TO_SANDBOX]: translateFn('Add to Sandbox'),
        [CustomRulesAutomaticActionsType.KILL_PROCESS]: translateFn('Kill process'),
        [CustomRulesAutomaticActionsType.SCAN]: translateFn('Antimalware scan'),
        [CustomRulesAutomaticActionsType.QUARANTINE]: translateFn('Quarantine'),
        [CustomRulesAutomaticActionsType.RISK_SCAN]: translateFn('Risk scan'),
      };
    }

    return this.translations.customRulesAutomaticActionsTypes[type];
  }

  public getCustomRulesAutomaticActionsWarning(type: CustomRulesAutomaticActionsType): string | null {
    if (!this.translations.customRulesAutomaticActionsWarnings) {
      this.translations.customRulesAutomaticActionsWarnings = {
        [CustomRulesAutomaticActionsType.ISOLATE]: translateFn('Enabling the Isolate action may cause outages.'),
        [CustomRulesAutomaticActionsType.KILL_PROCESS]: translateFn(
          'This action cannot be undone and may cause outages.'
        ),
        [CustomRulesAutomaticActionsType.SCAN]: translateFn('Full scans can take a long time to complete.'),
        [CustomRulesAutomaticActionsType.QUARANTINE]: translateFn(
          'Enabling the Quarantine action may cause outages. A kill process is required before the quarantine action.'
        ),
      };
    }

    return this.translations.customRulesAutomaticActionsWarnings[type] ?? null;
  }

  public getCustomRulesAutomaticActionsDetails(type: CustomRulesAutomaticActionsType): string {
    if (!this.translations.customRulesAutomaticActionsDetails) {
      this.translations.customRulesAutomaticActionsDetails = {
        [CustomRulesAutomaticActionsType.ISOLATE]: translateFn(
          'Disconnect the endpoint where detection occurred from the network.'
        ),
        [CustomRulesAutomaticActionsType.COLLECT_INVESTIGATION_PACKAGE]: translateFn(
          'Collect relevant data and logs from the system. No further action is taken.'
        ),
        [CustomRulesAutomaticActionsType.ADD_TO_SANDBOX]: translateFn(
          'Send the file that triggered the detection to the Sandbox Analyzer.'
        ),
        [CustomRulesAutomaticActionsType.KILL_PROCESS]: translateFn(
          'Terminate the process that triggered the detection.'
        ),
        [CustomRulesAutomaticActionsType.SCAN]: translateFn(
          'Run an on-demand scan on the endpoint with the detection.'
        ),
        [CustomRulesAutomaticActionsType.QUARANTINE]: translateFn(
          'Quarantine the file or process that triggered the detection.'
        ),
        [CustomRulesAutomaticActionsType.RISK_SCAN]: translateFn('Run a risk scan on the endpoint with the detection.'),
      };
    }

    return this.translations.customRulesAutomaticActionsDetails[type];
  }

  public getGenericErrorMessage() {
    return this.dependencies.translateFn(
      'An unexpected error has occurred. Please try again. If the issue persists, contact Bitdefender Technical Support.'
    );
  }

  public getAuthTypes(authType: AUTH_VALUES): string {
    const translations = {
      [AUTH_VALUES.PASSWORD]: translateFn('Password'),
      [AUTH_VALUES.PEM_FILE]: translateFn('Upload .pem file'),
    };

    return translations[authType];
  }

  public getEASMStrings(): object {
    return {
      scanConfiguration: this.dependencies.translateFn('Scan configuration'),
      changeScanAction: this.dependencies.translateFn('Scan action'),
      changeInvestigationStatus: this.dependencies.translateFn('Change status'),
      assign: this.dependencies.translateFn('Assign'),
      prioritize: this.dependencies.translateFn('Prioritize'),
      noResultBtn: this.dependencies.translateFn('Scan configuration'),
      noResultMsgDescription: this.dependencies.translateFn('Schedule or run a scan now to discover your findings.'),
      noResultMsgTitleAssets: this.dependencies.translateFn('No assets discovered'),
      noResultMsgTitleArtifacts: this.dependencies.translateFn('No artifacts discovered'),
      easmAssetsInventoryPageTitle: this.dependencies.translateFn('EASM assets'),
      easmArtifactsInventoryPageTitle: this.dependencies.translateFn('EASM artifacts'),
      noEligibleTargetsMessage: this.dependencies.translateFn('No eligible targets'),
    };
  }

  public translateEASMType(type: FindingType): string {
    if (!this.translations.EASMTypes) {
      this.translations.EASMTypes = {
        // assets
        [FindingType.IP_4]: translateFn('IPv4'),
        [FindingType.IP_6]: translateFn('IPv6'),
        [FindingType.DOMAIN]: translateFn('Domain'),
        [FindingType.EMAIL]: translateFn('Email'),
        [FindingType.SIMILAR_DOMAIN]: translateFn('Similar domain'),

        // artifacts
        [FindingType.ASN_REPORT]: translateFn('ASN report'),
        [FindingType.CERTIFICATE_REPORT]: translateFn('Certificate'),
        [FindingType.DNS_RECORD]: translateFn('DNS record'),
        [FindingType.IP_BLOCK]: translateFn('IP block'),
        [FindingType.SERVICE]: translateFn('Service'),
      };
    }
    return this.translations.EASMTypes[type];
  }

  public translateGroupedFindingCounter(type: FindingType, counter: number): string {
    if (counter === 1) {
      switch (type) {
        case FindingType.IP_4:
          return translateFn('1 IP');
        case FindingType.IP_6:
          return translateFn('1 IP');
        case FindingType.DOMAIN:
          return translateFn('1 domain');
        case FindingType.EMAIL:
          return translateFn('1 email');
        case FindingType.SIMILAR_DOMAIN:
          return translateFn('1 similar domain');
        case FindingType.ASN_REPORT:
          return translateFn('1 ASN');
        case FindingType.CERTIFICATE_REPORT:
          return translateFn('1 certificate');
        case FindingType.DNS_RECORD:
          return translateFn('1 DNS record');
        case FindingType.IP_BLOCK:
          return translateFn('1 IP block');
        case FindingType.SERVICE:
          return translateFn('1 service');
      }
    } else {
      switch (type) {
        case FindingType.IP_4:
          return translateFn('{counter} IPv4 addresses', { counter });
        case FindingType.IP_6:
          return translateFn('{counter} IPv6 addresses', { counter });
        case FindingType.DOMAIN:
          return translateFn('{counter} domains', { counter });
        case FindingType.EMAIL:
          return translateFn('{counter} emails', { counter });
        case FindingType.SIMILAR_DOMAIN:
          return translateFn('{counter} similar domains', { counter });
        case FindingType.ASN_REPORT:
          return translateFn('{counter} ASNs', { counter });
        case FindingType.CERTIFICATE_REPORT:
          return translateFn('{counter} certificates', { counter });
        case FindingType.DNS_RECORD:
          return translateFn('{counter} DNS records', { counter });
        case FindingType.IP_BLOCK:
          return translateFn('{counter} IP blocks', { counter });
        case FindingType.SERVICE:
          return translateFn('{counter} services', { counter });
      }
    }
  }

  public translateEASMFindingTypePluralized(type: FindingType): string {
    if (!this.translations.EASMFindingTypesPluralized) {
      this.translations.EASMFindingTypesPluralized = {
        [FindingType.ASN_REPORT]: 'ASNs',
        [FindingType.CERTIFICATE_REPORT]: 'Certificates',
        [FindingType.DOMAIN]: 'Domains',
        [FindingType.SIMILAR_DOMAIN]: 'Similar domains',
        [FindingType.DNS_RECORD]: 'DNS records',
        [FindingType.IP_BLOCK]: 'IP blocks',
        [FindingType.IP_4]: 'IPs',
        [FindingType.IP_6]: 'IPs',
        [FindingType.EMAIL]: 'Emails',
        [FindingType.SERVICE]: 'Services',
      };
    }

    return this.translations.EASMFindingTypesPluralized[type];
  }

  public translateEASMCountryType(type: EASMCountryType): string {
    if (!this.translations.EASMCountryTypes) {
      this.translations.EASMCountryTypes = {
        [EASMCountryType.ANDORRA]: translateFn('Andorra'),
        [EASMCountryType.UNITED_ARAB_EMIRATES]: translateFn('United Arab Emirates'),
        [EASMCountryType.AFGHANISTAN]: translateFn('Afghanistan'),
        [EASMCountryType.ANTIGUA_AND_BARBUDA]: translateFn('Antigua and Barbuda'),
        [EASMCountryType.ANGUILLA]: translateFn('Anguilla'),
        [EASMCountryType.ALBANIA]: translateFn('Albania'),
        [EASMCountryType.ARMENIA]: translateFn('Armenia'),
        [EASMCountryType.ANGOLA]: translateFn('Angola'),
        [EASMCountryType.ANTARCTICA]: translateFn('Antarctica'),
        [EASMCountryType.ARGENTINA]: translateFn('Argentina'),
        [EASMCountryType.AMERICAN_SAMOA]: translateFn('American Samoa'),
        [EASMCountryType.AUSTRIA]: translateFn('Austria'),
        [EASMCountryType.AUSTRALIA]: translateFn('Australia'),
        [EASMCountryType.ARUBA]: translateFn('Aruba'),
        [EASMCountryType.ALAND_ISLANDS]: translateFn('Aland Islands'),
        [EASMCountryType.AZERBAIJAN]: translateFn('Azerbaijan'),
        [EASMCountryType.BOSNIA_AND_HERZEGOVINA]: translateFn('Bosnia and Herzegovina'),
        [EASMCountryType.BARBADOS]: translateFn('Barbados'),
        [EASMCountryType.BANGLADESH]: translateFn('Bangladesh'),
        [EASMCountryType.BELGIUM]: translateFn('Belgium'),
        [EASMCountryType.BURKINA_FASO]: translateFn('Burkina Faso'),
        [EASMCountryType.BULGARIA]: translateFn('Bulgaria'),
        [EASMCountryType.BAHRAIN]: translateFn('Bahrain'),
        [EASMCountryType.BURUNDI]: translateFn('Burundi'),
        [EASMCountryType.BENIN]: translateFn('Benin'),
        [EASMCountryType.SAINT_BARTELEMEY]: translateFn('Saint Barthelemy'),
        [EASMCountryType.BERMUDA]: translateFn('Bermuda'),
        [EASMCountryType.BRUNEI_DARUSSALAM]: translateFn('Brunei Darussalam'),
        [EASMCountryType.BOLIVIA]: translateFn('Bolivia'),
        [EASMCountryType.BONAIRE_SAINT_EUSTATIUS_AND_SABA]: translateFn('Bonaire, Saint Eustatius and Saba'),
        [EASMCountryType.BRAZIL]: translateFn('Brazil'),
        [EASMCountryType.BAHAMAS]: translateFn('Bahamas'),
        [EASMCountryType.BHUTAN]: translateFn('Bhutan'),
        [EASMCountryType.BOUVET_ISLAND]: translateFn('Bouvet Island'),
        [EASMCountryType.BOTSWANA]: translateFn('Botswana'),
        [EASMCountryType.BELARUS]: translateFn('Belarus'),
        [EASMCountryType.BELIZE]: translateFn('Belize'),
        [EASMCountryType.CANADA]: translateFn('Canada'),
        [EASMCountryType.COCOS_KEELING_ISLANDS]: translateFn('Cocos (Keeling) Islands'),
        [EASMCountryType.CONGO_THE_DEMOCRATIC_REPUBLIC_OF_THE]: translateFn('Congo, The Democratic Republic of the'),
        [EASMCountryType.CENTRAL_AFRICAN_REPUBLIC]: translateFn('Central African Republic'),
        [EASMCountryType.CONGO]: translateFn('Congo'),
        [EASMCountryType.SWITZERLAND]: translateFn('Switzerland'),
        [EASMCountryType.COTE_D_IVOIRE]: translateFn("Cote d'Ivoire"),
        [EASMCountryType.COOK_ISLANDS]: translateFn('Cook Islands'),
        [EASMCountryType.CHILE]: translateFn('Chile'),
        [EASMCountryType.CAMEROON]: translateFn('Cameroon'),
        [EASMCountryType.CHINA]: translateFn('China'),
        [EASMCountryType.COLOMBIA]: translateFn('Colombia'),
        [EASMCountryType.COSTA_RICA]: translateFn('Costa Rica'),
        [EASMCountryType.CAPE_VERDE]: translateFn('Cape Verde'),
        [EASMCountryType.CURACAO]: translateFn('Curacao'),
        [EASMCountryType.CHRISTMAS_ISLAND]: translateFn('Christmas Island'),
        [EASMCountryType.CYPRUS]: translateFn('Cyprus'),
        [EASMCountryType.CZECH_REPUBLIC]: translateFn('Czech Republic'),
        [EASMCountryType.GERMANY]: translateFn('Germany'),
        [EASMCountryType.DJIBOUTI]: translateFn('Djibouti'),
        [EASMCountryType.DENMARK]: translateFn('Denmark'),
        [EASMCountryType.DOMINICA]: translateFn('Dominica'),
        [EASMCountryType.DOMINICAN_REPUBLIC]: translateFn('Dominican Republic'),
        [EASMCountryType.ALGERIA]: translateFn('Algeria'),
        [EASMCountryType.ECUADOR]: translateFn('Ecuador'),
        [EASMCountryType.ESTONIA]: translateFn('Estonia'),
        [EASMCountryType.EGYPT]: translateFn('Egypt'),
        [EASMCountryType.WESTERN_SAHARA]: translateFn('Western Sahara'),
        [EASMCountryType.ERITREA]: translateFn('Eritrea'),
        [EASMCountryType.SPAIN]: translateFn('Spain'),
        [EASMCountryType.ETHIOPIA]: translateFn('Ethiopia'),
        [EASMCountryType.FINLAND]: translateFn('Finland'),
        [EASMCountryType.FIJI]: translateFn('Fiji'),
        [EASMCountryType.FALKLAND_ISLANDS_MALVINAS]: translateFn('Falkland Islands (Malvinas)'),
        [EASMCountryType.FEDERATED_STATES_OF_MICRONESIA]: translateFn('Federated States of Micronesia'),
        [EASMCountryType.FAROE_ISLANDS]: translateFn('Faroe Islands'),
        [EASMCountryType.FRANCE]: translateFn('France'),
        [EASMCountryType.GABON]: translateFn('Gabon'),
        [EASMCountryType.UNITED_KINGDOM]: translateFn('United Kingdom'),
        [EASMCountryType.GRENADA]: translateFn('Grenada'),
        [EASMCountryType.GEORGIA]: translateFn('Georgia'),
        [EASMCountryType.FRENCH_GUIANA]: translateFn('French Guiana'),
        [EASMCountryType.GUERNSEY]: translateFn('Guernsey'),
        [EASMCountryType.GHANA]: translateFn('Ghana'),
        [EASMCountryType.GIBRALTAR]: translateFn('Gibraltar'),
        [EASMCountryType.GREENLAND]: translateFn('Greenland'),
        [EASMCountryType.GAMBIA]: translateFn('Gambia'),
        [EASMCountryType.GUINEA]: translateFn('Guinea'),
        [EASMCountryType.GUADELOUPE]: translateFn('Guadeloupe'),
        [EASMCountryType.EQUATORIAL_GUINEA]: translateFn('Equatorial Guinea'),
        [EASMCountryType.GREECE]: translateFn('Greece'),
        [EASMCountryType.SOUTH_GEORGIA_AND_THE_SOUTH_SANDWICH_ISLANDS]: translateFn(
          'South Georgia and the South Sandwich Islands'
        ),
        [EASMCountryType.GUATEMALA]: translateFn('Guatemala'),
        [EASMCountryType.GUAM]: translateFn('Guam'),
        [EASMCountryType.GUINEA_BISSAU]: translateFn('Guinea-Bissau'),
        [EASMCountryType.GUYANA]: translateFn('Guyana'),
        [EASMCountryType.HONG_KONG]: translateFn('Hong Kong'),
        [EASMCountryType.HEARD_ISLAND_AND_MCDONALD_ISLANDS]: translateFn('Heard Island and McDonald Islands'),
        [EASMCountryType.HONDURAS]: translateFn('Honduras'),
        [EASMCountryType.CROATIA]: translateFn('Croatia'),
        [EASMCountryType.HAITI]: translateFn('Haiti'),
        [EASMCountryType.HUNGARY]: translateFn('Hungary'),
        [EASMCountryType.INDONESIA]: translateFn('Indonesia'),
        [EASMCountryType.IRELAND]: translateFn('Ireland'),
        [EASMCountryType.ISRAEL]: translateFn('Israel'),
        [EASMCountryType.ISLE_OF_MAN]: translateFn('Isle of Man'),
        [EASMCountryType.INDIA]: translateFn('India'),
        [EASMCountryType.BRITISH_INDIAN_OCEAN_TERRITORY]: translateFn('British Indian Ocean Territory'),
        [EASMCountryType.IRAQ]: translateFn('Iraq'),
        [EASMCountryType.ICELAND]: translateFn('Iceland'),
        [EASMCountryType.ITALY]: translateFn('Italy'),
        [EASMCountryType.JERSEY]: translateFn('Jersey'),
        [EASMCountryType.JAMAICA]: translateFn('Jamaica'),
        [EASMCountryType.JORDAN]: translateFn('Jordan'),
        [EASMCountryType.JAPAN]: translateFn('Japan'),
        [EASMCountryType.KENYA]: translateFn('Kenya'),
        [EASMCountryType.KYRGYZSTAN]: translateFn('Kyrgyzstan'),
        [EASMCountryType.KOSOVO]: translateFn('Kosovo'),
        [EASMCountryType.CAMBODIA]: translateFn('Cambodia'),
        [EASMCountryType.KIRIBATI]: translateFn('Kiribati'),
        [EASMCountryType.COMOROS]: translateFn('Comoros'),
        [EASMCountryType.SAINT_KITTS_AND_NEVIS]: translateFn('Saint Kitts and Nevis'),
        [EASMCountryType.REPUBLIC_OF_KOREA]: translateFn('Republic of Korea'),
        [EASMCountryType.KUWAIT]: translateFn('Kuwait'),
        [EASMCountryType.CAYMAN_ISLANDS]: translateFn('Cayman Islands'),
        [EASMCountryType.KAZAKHSTAN]: translateFn('Kazakhstan'),
        [EASMCountryType.LAO_PEOPLE_S_DEMOCRATIC_REPUBLIC]: translateFn("Lao People's Democratic Republic"),
        [EASMCountryType.LEBANON]: translateFn('Lebanon'),
        [EASMCountryType.SAINT_LUCIA]: translateFn('Saint Lucia'),
        [EASMCountryType.LIECHTENSTEIN]: translateFn('Liechtenstein'),
        [EASMCountryType.SRI_LANKA]: translateFn('Sri Lanka'),
        [EASMCountryType.LIBERIA]: translateFn('Liberia'),
        [EASMCountryType.LESOTHO]: translateFn('Lesotho'),
        [EASMCountryType.LITHUANIA]: translateFn('Lithuania'),
        [EASMCountryType.LUXEMBOURG]: translateFn('Luxembourg'),
        [EASMCountryType.LATVIA]: translateFn('Latvia'),
        [EASMCountryType.LIBYAN_ARAB_JAMAHIRIYA]: translateFn('Libyan Arab Jamahiriya'),
        [EASMCountryType.MOROCCO]: translateFn('Morocco'),
        [EASMCountryType.MONACO]: translateFn('Monaco'),
        [EASMCountryType.REPUBLIC_OF_MOLDOVA]: translateFn('Republic of Moldova'),
        [EASMCountryType.MONTENEGRO]: translateFn('Montenegro'),
        [EASMCountryType.SAINT_MARTIN]: translateFn('Saint Martin'),
        [EASMCountryType.MADAGASCAR]: translateFn('Madagascar'),
        [EASMCountryType.MARSHALL_ISLANDS]: translateFn('Marshall Islands'),
        [EASMCountryType.MACEDONIA]: translateFn('Macedonia'),
        [EASMCountryType.MALI]: translateFn('Mali'),
        [EASMCountryType.MYANMAR]: translateFn('Myanmar'),
        [EASMCountryType.MONGOLIA]: translateFn('Mongolia'),
        [EASMCountryType.MACAO]: translateFn('Macao'),
        [EASMCountryType.NORTHERN_MARIANA_ISLANDS]: translateFn('Northern Mariana Islands'),
        [EASMCountryType.MARTINIQUE]: translateFn('Martinique'),
        [EASMCountryType.MAURITANIA]: translateFn('Mauritania'),
        [EASMCountryType.MONTSERRAT]: translateFn('Montserrat'),
        [EASMCountryType.MALTA]: translateFn('Malta'),
        [EASMCountryType.MAURITIUS]: translateFn('Mauritius'),
        [EASMCountryType.MALDIVES]: translateFn('Maldives'),
        [EASMCountryType.MALAWI]: translateFn('Malawi'),
        [EASMCountryType.MEXICO]: translateFn('Mexico'),
        [EASMCountryType.MALAYSIA]: translateFn('Malaysia'),
        [EASMCountryType.MOZAMBIQUE]: translateFn('Mozambique'),
        [EASMCountryType.NAMIBIA]: translateFn('Namibia'),
        [EASMCountryType.NEW_CALEDONIA]: translateFn('New Caledonia'),
        [EASMCountryType.NIGER]: translateFn('Niger'),
        [EASMCountryType.NORFOLK_ISLAND]: translateFn('Norfolk Island'),
        [EASMCountryType.NIGERIA]: translateFn('Nigeria'),
        [EASMCountryType.NICARAGUA]: translateFn('Nicaragua'),
        [EASMCountryType.NETHERLANDS]: translateFn('Netherlands'),
        [EASMCountryType.NORWAY]: translateFn('Norway'),
        [EASMCountryType.NEPAL]: translateFn('Nepal'),
        [EASMCountryType.NAURU]: translateFn('Nauru'),
        [EASMCountryType.NIUE]: translateFn('Niue'),
        [EASMCountryType.NEW_ZEALAND]: translateFn('New Zealand'),
        [EASMCountryType.OMAN]: translateFn('Oman'),
        [EASMCountryType.PANAMA]: translateFn('Panama'),
        [EASMCountryType.PERU]: translateFn('Peru'),
        [EASMCountryType.FRENCH_POLYNESIA]: translateFn('French Polynesia'),
        [EASMCountryType.PAPUA_NEW_GUINEA]: translateFn('Papua New Guinea'),
        [EASMCountryType.PHILIPPINES]: translateFn('Philippines'),
        [EASMCountryType.PAKISTAN]: translateFn('Pakistan'),
        [EASMCountryType.POLAND]: translateFn('Poland'),
        [EASMCountryType.SAINT_PIERRE_AND_MIQUELON]: translateFn('Saint Pierre and Miquelon'),
        [EASMCountryType.PITCAIRN]: translateFn('Pitcairn'),
        [EASMCountryType.PUERTO_RICO]: translateFn('Puerto Rico'),
        [EASMCountryType.PALESTINIAN_TERRITORY]: translateFn('Palestinian Territory'),
        [EASMCountryType.PORTUGAL]: translateFn('Portugal'),
        [EASMCountryType.PALAU]: translateFn('Palau'),
        [EASMCountryType.PARAGUAY]: translateFn('Paraguay'),
        [EASMCountryType.QATAR]: translateFn('Qatar'),
        [EASMCountryType.REUNION]: translateFn('Reunion'),
        [EASMCountryType.ROMANIA]: translateFn('Romania'),
        [EASMCountryType.SERBIA]: translateFn('Serbia'),
        [EASMCountryType.RUSSIAN_FEDERATION]: translateFn('Russian Federation'),
        [EASMCountryType.RWANDA]: translateFn('Rwanda'),
        [EASMCountryType.SAUDI_ARABIA]: translateFn('Saudi Arabia'),
        [EASMCountryType.SOLOMON_ISLANDS]: translateFn('Solomon Islands'),
        [EASMCountryType.SEYCHELLES]: translateFn('Seychelles'),
        [EASMCountryType.SUDAN]: translateFn('Sudan'),
        [EASMCountryType.SWEDEN]: translateFn('Sweden'),
        [EASMCountryType.SINGAPORE]: translateFn('Singapore'),
        [EASMCountryType.SAINT_HELENA]: translateFn('Saint Helena'),
        [EASMCountryType.SLOVENIA]: translateFn('Slovenia'),
        [EASMCountryType.SVALBARD_AND_JAN_MAYEN]: translateFn('Svalbard and Jan Mayen'),
        [EASMCountryType.SLOVAKIA]: translateFn('Slovakia'),
        [EASMCountryType.SIERRA_LEONE]: translateFn('Sierra Leone'),
        [EASMCountryType.SAN_MARINO]: translateFn('San Marino'),
        [EASMCountryType.SENEGAL]: translateFn('Senegal'),
        [EASMCountryType.SOMALIA]: translateFn('Somalia'),
        [EASMCountryType.SURINAME]: translateFn('Suriname'),
        [EASMCountryType.SOUTH_SUDAN]: translateFn('South Sudan'),
        [EASMCountryType.SAO_TOME_AND_PRINCIPE]: translateFn('Sao Tome and Principe'),
        [EASMCountryType.EL_SALVADOR]: translateFn('El Salvador'),
        [EASMCountryType.SINT_MAARTEN]: translateFn('Sint Maarten'),
        [EASMCountryType.SWAZILAND]: translateFn('Swaziland'),
        [EASMCountryType.TURKS_AND_CAICOS_ISLANDS]: translateFn('Turks and Caicos Islands'),
        [EASMCountryType.CHAD]: translateFn('Chad'),
        [EASMCountryType.FRENCH_SOUTHERN_TERRITORIES]: translateFn('French Southern Territories'),
        [EASMCountryType.TOGO]: translateFn('Togo'),
        [EASMCountryType.THAILAND]: translateFn('Thailand'),
        [EASMCountryType.TAJIKISTAN]: translateFn('Tajikistan'),
        [EASMCountryType.TOKELAU]: translateFn('Tokelau'),
        [EASMCountryType.TIMOR_LESTE]: translateFn('Timor-Leste'),
        [EASMCountryType.TURKMENISTAN]: translateFn('Turkmenistan'),
        [EASMCountryType.TUNISIA]: translateFn('Tunisia'),
        [EASMCountryType.TONGA]: translateFn('Tonga'),
        [EASMCountryType.TURKEY]: translateFn('Turkey'),
        [EASMCountryType.TRINIDAD_AND_TOBAGO]: translateFn('Trinidad and Tobago'),
        [EASMCountryType.TUVALU]: translateFn('Tuvalu'),
        [EASMCountryType.TAIWAN]: translateFn('Taiwan'),
        [EASMCountryType.UNITED_REPUBLIC_OF_TANZANIA]: translateFn('Tanzania, United Republic of'),
        [EASMCountryType.UKRAINE]: translateFn('Ukraine'),
        [EASMCountryType.UGANDA]: translateFn('Uganda'),
        [EASMCountryType.UNITED_STATES_MINOR_OUTLYING_ISLANDS]: translateFn('United States Minor Outlying Islands'),
        [EASMCountryType.UNITED_STATES]: translateFn('United States'),
        [EASMCountryType.URUGUAY]: translateFn('Uruguay'),
        [EASMCountryType.UZBEKISTAN]: translateFn('Uzbekistan'),
        [EASMCountryType.HOLY_SEE_VATICAN_CITY_STATE]: translateFn('Holy See (Vatican City State)'),
        [EASMCountryType.SAINT_VINCENT_AND_THE_GRENADINES]: translateFn('Saint Vincent and the Grenadines'),
        [EASMCountryType.VENEZUELA]: translateFn('Venezuela'),
        [EASMCountryType.VIRGIN_ISLANDS_BRITISH]: translateFn('Virgin Islands, British'),
        [EASMCountryType.VIRGIN_ISLANDS_U_S]: translateFn('Virgin Islands, U.S.'),
        [EASMCountryType.VIETNAM]: translateFn('Vietnam'),
        [EASMCountryType.VANUATU]: translateFn('Vanuatu'),
        [EASMCountryType.WALLIS_AND_FUTUNA]: translateFn('Wallis and Futuna'),
        [EASMCountryType.SAMOA]: translateFn('Samoa'),
        [EASMCountryType.YEMEN]: translateFn('Yemen'),
        [EASMCountryType.MAYOTTE]: translateFn('Mayotte'),
        [EASMCountryType.SOUTH_AFRICA]: translateFn('South Africa'),
        [EASMCountryType.ZAMBIA]: translateFn('Zambia'),
        [EASMCountryType.ZIMBABWE]: translateFn('Zimbabwe'),
      };
    }

    return this.translations.EASMCountryTypes[type];
  }

  public translateEASMDNSRecordType(type: DNSRecordType): string {
    if (!this.translations.EASMDNSRecordType) {
      this.translations.EASMDNSRecordType = {
        [DNSRecordType.SOA]: 'SOA',
        [DNSRecordType.MX]: 'MX',
        [DNSRecordType.NS]: 'NS',
        [DNSRecordType.CNAME]: 'CNAME',
      };
    }
    return this.translations.EASMDNSRecordType[type];
  }

  public translateNextScanInclusionStatus(status: NextScanInclusionStatus): string {
    if (!this.translations.nextScanInclusionStatus) {
      this.translations.nextScanInclusionStatus = {
        [NextScanInclusionStatus.EXCLUDED]: translateFn('Exclude from scan'),
        [NextScanInclusionStatus.INCLUDED]: translateFn('Include in scan'),
        [NextScanInclusionStatus.DEFAULT]: translateFn('No action taken'),
      };
    }

    return this.translations.nextScanInclusionStatus[status];
  }

  public translateExpirationStatus(status: ExpirationStatus): string {
    if (!this.translations.expirationStatus) {
      this.translations.expirationStatus = {
        [ExpirationStatus.EXPIRED]: translateFn('Expired'),
        [ExpirationStatus.SOON_TO_EXPIRE]: translateFn('Expires soon'),
        [ExpirationStatus.NOT_SOON_TO_EXPIRE]: translateFn('No action needed'),
      };
    }

    return this.translations.expirationStatus[status];
  }

  public translateExpirationStatusInAssets(status: ExpirationStatus): string {
    if (!this.translations.expirationStatusInAssets) {
      this.translations.expirationStatusInAssets = {
        [ExpirationStatus.EXPIRED]: translateFn('Contains expired certificates'),
        [ExpirationStatus.SOON_TO_EXPIRE]: translateFn('Contains soon to expire certificates'),
        [ExpirationStatus.NOT_SOON_TO_EXPIRE]: translateFn('No action needed'),
      };
    }

    return this.translations.expirationStatusInAssets[status];
  }

  public getTasksStrings() {
    const commonTitleSingleTask = translateFn('Delete selected task?');
    const commonMessageSingleTask = translateFn(
      'The task and its subtasks will be deleted. This action cannot be undone.'
    );
    const commonTitleMultipleTasks = translateFn('Delete selected tasks?');
    const commonMessageMultipleTasks = translateFn(
      'The tasks and their subtasks will be deleted. This action cannot be undone.'
    );

    return {
      deleteDialog: {
        [TaskSelectionStatus.SINGLE_PENDING]: {
          title: commonTitleSingleTask,
          message: commonMessageSingleTask,
        },
        [TaskSelectionStatus.SINGLE_IN_PROGRESS]: {
          title: translateFn('Delete pending subtasks?'),
          message: translateFn(
            'Cannot delete a task while in progress. Only pending subtasks will be deleted. This action cannot be undone.'
          ),
        },
        [TaskSelectionStatus.SINGLE_FINISHED]: {
          title: commonTitleSingleTask,
          message: commonMessageSingleTask,
        },
        [TaskSelectionStatus.ALL_PENDING]: {
          title: commonTitleMultipleTasks,
          message: commonMessageMultipleTasks,
        },
        [TaskSelectionStatus.ALL_IN_PROGRESS]: {
          title: translateFn('Delete pending subtasks?'),
          message: translateFn(
            'Cannot delete tasks while in progress. Only pending subtasks will be deleted. This action cannot be undone.'
          ),
        },
        [TaskSelectionStatus.ALL_FINISHED]: {
          title: commonTitleMultipleTasks,
          message: commonMessageMultipleTasks,
        },
        [TaskSelectionStatus.PENDING_AND_IN_PROGRESS]: {
          title: commonTitleMultipleTasks,
          message: translateFn(
            'Pending tasks and their subtasks will be deleted. For tasks in progress, only pending subtasks will be deleted. This action cannot be undone.'
          ),
        },
        [TaskSelectionStatus.PENDING_AND_FINISHED]: {
          title: commonTitleMultipleTasks,
          message: commonMessageMultipleTasks,
        },
        [TaskSelectionStatus.FINISHED_AND_IN_PROGRESS]: {
          title: commonTitleMultipleTasks,
          message: translateFn(
            'Finished tasks and their subtasks will be deleted. For tasks in progress, only pending subtasks will be deleted. This action cannot be undone.'
          ),
        },
        [TaskSelectionStatus.ALL_STATUSES]: {
          title: commonTitleMultipleTasks,
          message: translateFn(
            'Finished and pending tasks will be deleted along with their subtasks. For tasks in progress, only pending subtasks will be deleted. This action cannot be undone.'
          ),
        },
      },
    };
  }

  public getRiskManagementCompliance(complianceId: Compliance): string {
    const translations = {
      [Compliance.NONE]: translateFn('None'),
      [Compliance.CISv8]: 'CIS v8.0',
      [Compliance.SOC2]: 'SOC 2',
      [Compliance.ISO27001]: 'ISO/IEC 27001:2022',
      [Compliance.GDPR]: 'GDPR (EU)',
      [Compliance.NIS2]: 'NIS2 Directive (EU)',
      [Compliance.DORA]: 'DORA (EU)',
      [Compliance.BDCHBWINDOWS]: 'Cyber Hygiene - Windows',
      [Compliance.CMMC2]: 'CMMC 2.0 (US)',
      [Compliance.PCIDSSV4]: 'PCI DSS V4.0.1',
      [Compliance.HIPAA]: 'HIPAA (US)',
    };

    return translations[complianceId];
  }

  public getRiskManagementStatus(status: Status) {
    const translations = {
      [Status.COMPLIANT]: translateFn('Compliant'),
      [Status.DETECTED]: translateFn('Detected'),
    };

    return translations[status] ?? translateFn('N/A');
  }

  public getRiskManagementState(state: State) {
    const translations = {
      [State.ACTIVE]: translateFn('Active'),
      [State.IGNORED]: translateFn('Ignored'),
    };

    return translations[state];
  }

  public getRiskManagementWatchlist(watchlist: Watchlist) {
    const translations = {
      [Watchlist.YES]: translateFn('Yes'),
      [Watchlist.NO]: translateFn('No'),
    };

    return translations[watchlist];
  }

  public getRiskManagementUserType(userType: UserType) {
    const translations = {
      [UserType.LOCAL]: translateFn('Local'),
      [UserType.AD]: translateFn('Active Directory (AD)'),
      [UserType.IAM_USER]: translateFn('IAM user'),
      [UserType.ACTIVE_DIRECTORY_USER]: translateFn('Azure Active Directory (AAD)'),
      [UserType.IAM_ROLE]: translateFn('IAM role'),
      [UserType.IAM_USER_GROUP_ROLE]: translateFn('IAM user group role'),
      [UserType.IAM_GROUP]: translateFn('IAM group'),
      [UserType.ROOT_USER]: translateFn('Root user'),
      [UserType.IAM_SERVICE_ACCOUNT]: translateFn('IAM service account'),
    };

    return translations[userType];
  }

  public getRiskManagementMitigationType(mitigationType: MitigationType) {
    const translations = {
      [MitigationType.MANUAL]: translateFn('Manual'),
      [MitigationType.AUTOMATIC]: translateFn('Automatic'),
    };

    return translations[mitigationType];
  }

  public getOperatingSystem(operatingSystem: OperatingSystemType | CloudProviders) {
    const translations = {
      [OperatingSystemType.UNKNOWN]: translateFn('Unknown'),
      [OperatingSystemType.IOS]: translateFn('IOS'),
      [OperatingSystemType.ANDROID]: translateFn('Android'),
      [OperatingSystemType.WINDOWS]: translateFn('Windows'),
      [OperatingSystemType.LINUX]: translateFn('Linux'),
      [OperatingSystemType.SOLARIS]: translateFn('Solaris'),
      [OperatingSystemType.MACOSX]: translateFn('macOS'),
      [OperatingSystemType.CONTAINER]: translateFn('Container'),
      [CloudProviders.AWS]: translateFn('AWS'),
      [CloudProviders.GCP]: translateFn('GCP'),
      [CloudProviders.AZURE]: translateFn('Azure'),
      [AffectedDeviceType.K8S_NAMESPACE]: translateFn('Kubernetes'),
    };

    return translations[operatingSystem];
  }

  public getDeviceTypes(deviceType: AffectedDeviceType): string {
    const translations = {
      [AffectedDeviceType.ENDPOINT]: translateFn('Endpoint'),
      [AffectedDeviceType.SERVER]: translateFn('Server'),

      // This texts should not be translated, they are used as is
      [AffectedDeviceType.MONITOR_ACTIVITY_LOG_ALERTS]: 'Monitor/activityLogAlerts',
      [AffectedDeviceType.AKS_MANAGED_CLUSTER]: 'Aks/managedCluster',
      [AffectedDeviceType.APPLICATION_GATEWAY_LISTENER]: 'ApplicationGateway/listener',
      [AffectedDeviceType.APP_SERVICE_FUNCTION_APP]: 'AppService/functionApp',
      [AffectedDeviceType.APP_SERVICE_SITE]: 'AppService/site',
      [AffectedDeviceType.AUTHORIZATION_ROLE_DEFINITIONS]: 'Authorization/roleDefinitions',
      [AffectedDeviceType.CACHE_REDIS]: 'Cache/redis',
      [AffectedDeviceType.COMPUTE_DISKS]: 'Compute/disks',
      [AffectedDeviceType.COMPUTE_VIRTUAL_MACHINES]: 'Compute/virtualMachines',
      [AffectedDeviceType.CONTAINER_INSTANCE_INSTANCE]: 'ContainerInstance/instance',
      [AffectedDeviceType.INSIGHTS_DIAGNOSTIC_SETTINGS]: 'Insights/diagnosticSettings',
      [AffectedDeviceType.INSIGHTS_LOG_PROFILES]: 'Insights/logProfiles',
      [AffectedDeviceType.KEY_VAULT_KEYS]: 'KeyVault/keys',
      [AffectedDeviceType.KEY_VAULT_VAULTS]: 'KeyVault/vaults',
      [AffectedDeviceType.MARIA_DBSERVERS]: 'MariaDB/servers',
      [AffectedDeviceType.MYSQL_SERVERS]: 'Mysql/servers',
      [AffectedDeviceType.NETWORK_NETWORK_FLOW_LOGS]: 'Network/networkFlowLogs',
      [AffectedDeviceType.NETWORK_LOAD_BALANCERS]: 'Network/loadBalancers',
      [AffectedDeviceType.NETWORK_NETWORK_WATCHERS]: 'Network/networkWatchers',
      [AffectedDeviceType.NETWORK_PUBLIC_IPADDRESSES]: 'Network/publicIPAddresses',
      [AffectedDeviceType.NETWORK_VIRTUAL_NETWORKS]: 'Network/virtualNetworks',
      [AffectedDeviceType.NETWORK_NETWORK_SECURITY_GROUPS]: 'Network/networkSecurityGroups',
      [AffectedDeviceType.NETWORK_VIRTUAL_NETWORKS_SUBNETS]: 'Network/virtualNetworksSubnets',
      [AffectedDeviceType.NETWORK_ROUTE_TABLES]: 'Network/routeTables',
      [AffectedDeviceType.NETWORK_NETWORK_INTERFACES]: 'Network/networkInterfaces',
      [AffectedDeviceType.POSTGRESQL_SERVERS]: 'Postgresql/servers',
      [AffectedDeviceType.RESOURCES_RESOURCE_GROUPS]: 'Resources/resourceGroups',
      [AffectedDeviceType.SECURITY_AUTO_PROVISIONING_SETTINGS]: 'Security/autoProvisioningSettings',
      [AffectedDeviceType.SECURITY_PRICING]: 'Security/pricing',
      [AffectedDeviceType.SECURITY_SECURITY_CONTACTS]: 'Security/securityContacts',
      [AffectedDeviceType.SQL_DATABASES]: 'Sql/databases',
      [AffectedDeviceType.SQL_SERVERS]: 'Sql/servers',
      [AffectedDeviceType.STORAGE_STORAGE_ACCOUNTS]: 'Storage/storageAccounts',
      [AffectedDeviceType.DOCUMENT_DBDATABASE_ACCOUNTS]: 'DocumentDB/databaseAccounts',
      [AffectedDeviceType.ACMCERTIFICATE]: 'ACM Certificate',
      [AffectedDeviceType.AUTO_SCALING_GROUP]: 'Auto Scaling Group',
      [AffectedDeviceType.AUTO_SCALING_LAUNCH_CONFIGURATION]: 'AutoScaling Launch Configuration',
      [AffectedDeviceType.CLOUD_FRONT_DISTRIBUTION]: 'CloudFront Distribution',
      [AffectedDeviceType.CLOUD_TRAIL_TRAIL]: 'CloudTrail Trail',
      [AffectedDeviceType.CLOUD_WATCH_ALARM]: 'CloudWatch Alarm',
      [AffectedDeviceType.CONFIGURATION_RECORDER]: 'Configuration Recorder',
      [AffectedDeviceType.DYNAMO_DBTABLE]: 'DynamoDB Table',
      [AffectedDeviceType.EBSSNAPSHOT]: 'EBS Snapshot',
      [AffectedDeviceType.EBSVOLUME]: 'EBS Volume',
      [AffectedDeviceType.ECRREPOSITORY]: 'ECR Repository',
      [AffectedDeviceType.ECSCLUSTER]: 'ECS Cluster',
      [AffectedDeviceType.ECSCONTAINER_INSTANCE]: 'ECS Container Instance',
      [AffectedDeviceType.ECSTASK_DEFINITION_ACTIVE]: 'ECS Task Definition (Active)',
      [AffectedDeviceType.ECSTASK_DEFINITION_FAMILY]: 'ECS Task Definition Family',
      [AffectedDeviceType.ECSSERVICE]: 'ECS Service',
      [AffectedDeviceType.EC2_AMI]: 'EC2 AMI',
      [AffectedDeviceType.EC2_INSTANCE]: 'EC2 Instance',
      [AffectedDeviceType.EC2_ELASTIC_IPADDRESS]: 'EC2 Elastic IP Address',
      [AffectedDeviceType.EC2_NETWORK_ACL]: 'EC2 Network ACL',
      [AffectedDeviceType.EC2_SECURITY_GROUP]: 'EC2 Security Group',
      [AffectedDeviceType.EC2_SUBNET]: 'EC2 Subnet',
      [AffectedDeviceType.EFSFILE_SYSTEM]: 'EFS File System',
      [AffectedDeviceType.EKSCLUSTER]: 'EKS Cluster',
      [AffectedDeviceType.ELASTI_CACHE_CLUSTER]: 'ElastiCache Cluster',
      [AffectedDeviceType.ELASTIC_BEANSTALK_ENVIRONMENT]: 'Elastic Beanstalk Environment',
      [AffectedDeviceType.ELASTIC_SEARCH_DOMAIN]: 'ElasticSearch Domain',
      [AffectedDeviceType.APPLICATION_LOAD_BALANCER]: 'Application Load Balancer',
      [AffectedDeviceType.NETWORK_LOAD_BALANCER]: 'Network Load Balancer',
      [AffectedDeviceType.ELASTIC_LOAD_BALANCERV2]: 'Elastic Load Balancer v2',
      [AffectedDeviceType.CLASSIC_LOAD_BALANCER]: 'Classic Load Balancer',
      [AffectedDeviceType.GUARD_DUTY_DETECTOR]: 'GuardDuty Detector',
      [AffectedDeviceType.IAMACCESS_ANALYZER_ANALYZER]: 'IAM AccessAnalyzer Analyzer',
      [AffectedDeviceType.IAMCERTIFICATE]: 'IAM Certificate',
      [AffectedDeviceType.IAMIDENTITY_PROVIDER]: 'IAM Identity Provider',
      [AffectedDeviceType.IAMPASSWORD_POLICY]: 'IAM Password Policy',
      [AffectedDeviceType.IAMPOLICY]: 'IAM Policy',
      [AffectedDeviceType.KINESIS_DATA_STREAM]: 'Kinesis Data Stream',
      [AffectedDeviceType.KMSKEY]: 'KMS Key',
      [AffectedDeviceType.LAMBDA_FUNCTION]: 'Lambda Function',
      [AffectedDeviceType.ORGANIZATIONS_ORGANIZATION]: 'Organizations Organization',
      [AffectedDeviceType.RDSDBINSTANCE]: 'RDS DB Instance',
      [AffectedDeviceType.RDSDBSNAPSHOT]: 'RDS DB Snapshot',
      [AffectedDeviceType.RDSDBCLUSTER]: 'RDS DB Cluster',
      [AffectedDeviceType.REDSHIFT_CLUSTER]: 'Redshift Cluster',
      [AffectedDeviceType.ROUTE53_HOSTED_ZONE]: 'Route 53 Hosted Zone',
      [AffectedDeviceType.S3_BUCKET]: 'S3 Bucket',
      [AffectedDeviceType.SAGE_MAKER_NOTEBOOK_INSTANCE]: 'SageMaker Notebook Instance',
      [AffectedDeviceType.SECRETS_MANAGER_SECRET]: 'SecretsManager Secret',
      [AffectedDeviceType.SNSTOPIC]: 'SNS Topic',
      [AffectedDeviceType.SNSSUBSCRIPTION]: 'SNS Subscription',
      [AffectedDeviceType.SQSQUEUE]: 'SQS Queue',
      [AffectedDeviceType.SSMPARAMETER]: 'SSM Parameter',
      [AffectedDeviceType.SSMSESSION]: 'SSM Session',
      [AffectedDeviceType.SSOINSTANCE]: 'SSO Instance',
      [AffectedDeviceType.SSOPERMISSION_SET]: 'SSO Permission Set',
      [AffectedDeviceType.VPCENDPOINT]: 'VPC Endpoint',
      [AffectedDeviceType.VPCEGRESS_ONLY_INTERNET_GATEWAY]: 'VPC Egress-Only Internet Gateway',
      [AffectedDeviceType.VPCINTERNET_GATEWAY]: 'VPC Internet Gateway',
      [AffectedDeviceType.VPCVPC]: 'VPC Vpc',
      [AffectedDeviceType.WAFV2_ACCESS_CONTROL_LIST]: 'WAFv2 Access Control List',
      [AffectedDeviceType.WORK_SPACES_WORK_SPACE]: 'WorkSpaces WorkSpace',
      [AffectedDeviceType.APP_ENGINE_FIREWALL_RULE]: 'AppEngine Firewall Rule',
      [AffectedDeviceType.BIG_QUERY_DATASET]: 'BigQuery Dataset',
      [AffectedDeviceType.BIG_QUERY_TABLE]: 'BigQuery Table',
      [AffectedDeviceType.CLOUD_FUNCTIONS_FUNCTION]: 'Cloud Functions Function',
      [AffectedDeviceType.CLOUD_RUN_SERVICE]: 'Cloud Run Service',
      [AffectedDeviceType.COMPUTE_FIREWALL_RULE]: 'Compute Firewall Rule',
      [AffectedDeviceType.COMPUTE_GLOBAL_BACKEND_SERVICE]: 'Compute Global Backend Service',
      [AffectedDeviceType.COMPUTE_HTTPSTARGET_PROXY]: 'Compute HTTPS Target Proxy',
      [AffectedDeviceType.COMPUTE_INSTANCE]: 'Compute Instance',
      [AffectedDeviceType.COMPUTE_NATGATEWAY]: 'Compute NAT Gateway',
      [AffectedDeviceType.COMPUTE_NETWORK]: 'Compute Network',
      [AffectedDeviceType.COMPUTE_PERSISTENT_DISK]: 'Compute Persistent Disk',
      [AffectedDeviceType.COMPUTE_PROJECT]: 'Compute Project',
      [AffectedDeviceType.COMPUTE_SSLPOLICY]: 'Compute SSL Policy',
      [AffectedDeviceType.COMPUTE_SUBNET]: 'Compute Subnet',
      [AffectedDeviceType.COMPUTE_ZONAL_INSTANCE_GROUP]: 'Compute Zonal Instance Group',
      [AffectedDeviceType.GKECONTAINER_CLUSTER]: 'GKE Container Cluster',
      [AffectedDeviceType.GKECONTAINER_NODE_POOL]: 'GKE Container Node Pool',
      [AffectedDeviceType.DATA_PROC_CLUSTER]: 'DataProc Cluster',
      [AffectedDeviceType.CLOUD_DNSMANAGED_ZONE]: 'Cloud DNS Managed Zone',
      [AffectedDeviceType.KMSCRYPTO_KEY]: 'KMS CryptoKey',
      [AffectedDeviceType.CLOUD_LOGGING_LOG_METRIC]: 'Cloud Logging Log Metric',
      [AffectedDeviceType.CLOUD_LOGGING_LOG_SINK]: 'Cloud Logging Log Sink',
      [AffectedDeviceType.CLOUD_MONITORING_ALERT_POLICY]: 'Cloud Monitoring Alert Policy',
      [AffectedDeviceType.PUB_SUB_TOPIC]: 'PubSub Topic',
      [AffectedDeviceType.PUB_SUB_SUBSCRIPTION]: 'PubSub Subscription',
      [AffectedDeviceType.REDIS_INSTANCE]: 'Redis Instance',
      [AffectedDeviceType.RESOURCE_MANAGER_PROJECT]: 'Resource Manager Project',
      [AffectedDeviceType.SERVICE_USAGE_SERVICE]: 'Service Usage Service',
      [AffectedDeviceType.CLOUD_SQLINSTANCE]: 'Cloud SQL Instance',
      [AffectedDeviceType.STORAGE_BUCKET]: 'Storage Bucket',
      [AffectedDeviceType.BIGTABLE_CLUSTER]: 'Bigtable Cluster',
      [AffectedDeviceType.API_GATEWAY_REST_API]: 'ApiGateway REST API',
      [AffectedDeviceType.ELASTIC_LOAD_BALANCER_V2_TARGET_GROUP]: 'Elastic Load Balancer v2 Target Group',
      [AffectedDeviceType.EC2_ROUTE_TABLE]: 'EC2 Route Table',
      [AffectedDeviceType.EC2_NETWORK_INTERFACE]: 'EC2 Network Interface',

      [AffectedDeviceType.K8S_NAMESPACE]: 'Namespace',
      [AffectedDeviceType.K8S_POD]: 'Pod',
      [AffectedDeviceType.K8S_SECRET]: 'Secret',
      [AffectedDeviceType.K8S_SERVICE_ACCOUNT]: 'ServiceAccount',
      [AffectedDeviceType.K8S_MUTATING_WEBHOOK_CONFIGURATION]: 'MutatingWebhookConfiguration',
      [AffectedDeviceType.K8S_VALIDATING_WEBHOOK_CONFIGURATION]: 'ValidatingWebhookConfiguration',
      [AffectedDeviceType.K8S_DAEMON_SET]: 'DaemonSet',
      [AffectedDeviceType.K8S_DEPLOYMENT]: 'Deployment',
      [AffectedDeviceType.K8S_REPLICA_SET]: 'ReplicaSet',
      [AffectedDeviceType.K8S_STATEFUL_SET]: 'StatefulSet',
      [AffectedDeviceType.K8S_CRON_JOB]: 'CronJob',
      [AffectedDeviceType.K8S_JOB]: 'Job',
      [AffectedDeviceType.K8S_CLUSTER_ROLE_BINDING]: 'ClusterRoleBinding',
      [AffectedDeviceType.K8S_CLUSTER_ROLE]: 'ClusterRole',
      [AffectedDeviceType.K8S_ROLE_BINDING]: 'RoleBinding',
      [AffectedDeviceType.K8S_ROLE]: 'Role',
      [AffectedDeviceType.K8S_KUBELET_INFO]: 'KubeletInfo',
      [AffectedDeviceType.K8S_CONTROL_PLANE_INFO]: 'ControlPlaneInfo',
      [AffectedDeviceType.K8S_KUBE_PROXY_INFO]: 'KubeProxyInfo',

      [AffectedDeviceType.CONTAINER_IMAGE]: 'Container image',
      [AffectedDeviceType.CONTAINER]: 'Container',
    };

    return translations[deviceType];
  }

  public getMisconfigurationType(misconfigurationType: MisconfigurationType) {
    const translations = {
      [MisconfigurationType.BROWSER]: translateFn('Browser'),
      [MisconfigurationType.NETWORK]: translateFn('Network'),
      [MisconfigurationType.OS]: translateFn('OS'),
      [MisconfigurationType.VULNERABILITY]: translateFn('Vulnerability'),
      [MisconfigurationType.CLOUD]: translateFn('Cloud'),
      [MisconfigurationType.ACCESS_CONTROL]: translateFn('Access control'),
      [MisconfigurationType.CONTROL_PLANE]: translateFn('Control plane'),
      [MisconfigurationType.SECRETS]: translateFn('Secrets'),
      [MisconfigurationType.WORKLOAD]: translateFn('Workload'),
    };

    return translations[misconfigurationType];
  }

  public getRiskManagementApplicationType(applicationType: ApplicationType) {
    const translations = {
      [ApplicationType.APPLICATION]: translateFn('Application'),
      [ApplicationType.OS]: translateFn('OS'),
      [ApplicationType.SERVICES]: translateFn('Service'),
    };

    return translations[applicationType];
  }

  public getRiskManagementSource(source: VulnerabilitiesSources) {
    const translations = {
      [VulnerabilitiesSources.ENDPOINT]: translateFn('Endpoint scanner'),
      [VulnerabilitiesSources.NSVA]: translateFn('Network scanner'),
    };

    return translations[source];
  }

  public getRiskManagementAnswerType(answerType: AnswerType) {
    const translations = {
      [AnswerType.YES]: translateFn('Yes'),
      [AnswerType.NO]: translateFn('No'),
    };

    return translations[answerType];
  }

  public getControlStatus(controlStatus: ControlStatus) {
    const translations = {
      [ControlStatus.PASSED]: translateFn('Passed'),
      [ControlStatus.FAILED]: translateFn('Failed'),
      [ControlStatus.NOT_EVALUATED]: translateFn('Not evaluated'),
    };

    return translations[controlStatus];
  }

  public getRiskManagementScoreBreakdown(scoreType: string, category: string) {
    // These texts should be changed also in risk management dashboard, for top devices and top user.\
    const translations = {
      [RiskTypes.MISCONFIGURATIONS]: {
        [ScoreTypes.ENDPOINTS_SCORE]: translateFn('Found on multiple resources'),
      },
      [RiskTypes.VULNERABILITIES]: {
        [ScoreTypes.ENDPOINTS_SCORE]: translateFn('CVEs found on multiple resources'),
        [ScoreTypes.INDUSTRY_SCORE]: translateFn('CVEs exploited in your industry'),
        [ScoreTypes.EXPLOITABILITY_SCORE]: translateFn('CVEs have known exploits'),
        [ScoreTypes.CVES_COUNT_SCORE]: translateFn('CVE prevalence score'),
      },
      [RiskTypes.USER_BEHAVIOUR_RISKS]: {
        [ScoreTypes.ENDPOINTS_SCORE]: translateFn('Found on multiple resources'),
      },
      [RiskTypes.DEVICES]: {
        [ScoreTypes.APPLICATIONS_SCORE]: translateFn('Vulnerable applications prevalence score'),
        [ScoreTypes.USERS_SCORE]: translateFn('Logins by high risk identities in the past 30 days'),
      },
    };

    return translations[category][scoreType];
  }

  public translateRiskManagementFixRoutineResult(value: number) {
    const translations = {
      [FixRisksErrorCodes.SUCCESS]: translateFn('Finished'),
      [FixRisksErrorCodes.FAILED]: translateFn('Failed'),
    };

    return translations[value] ?? translateFn('Unknown');
  }

  public translateRiskManagementRollbackRoutineResult(value: number) {
    const translations = {
      [RollbackRisksErrorCodes.SUCCESS]: translateFn('Rollback successful.'),
      [RollbackRisksErrorCodes.ROLLBACK_CORRUPTED]: translateFn('Rollback target not found.'),
      [RollbackRisksErrorCodes.FAILED]: translateFn('Failed'),
      [RollbackRisksErrorCodes.ROLLBACK_NOT_FOUND]: translateFn('Rollback information not available.'),
      [RollbackRisksErrorCodes.ROLLBACK_CHANGED_STATE]: translateFn(
        'Unable to roll back. The target value has changed since the fix.'
      ),
    };

    return translations[value] ?? translateFn('Unknown');
  }

  public translateScanAction(scanAction: ScanAction): string {
    if (typeof this.translations.scanActions === 'undefined') {
      this.translations.scanActions = {
        [ScanAction.INCLUDE_IN_SCAN]: translateFn('Include in scan'),
        [ScanAction.EXCLUDE_FROM_SCAN]: translateFn('Exclude from scan'),
      };
    }

    return this.translations.scanActions[scanAction];
  }

  public translateInvestigationStatus(investigationStatus: InvestigationStatus): string {
    if (typeof this.translations.investigationStatuses === 'undefined') {
      this.translations.investigationStatuses = {
        [InvestigationStatus.OPEN]: translateFn('Open'),
        [InvestigationStatus.IN_PROGRESS]: translateFn('Investigating'),
        [InvestigationStatus.CLOSED]: translateFn('Closed'),
      };
    }

    return this.translations.investigationStatuses[investigationStatus];
  }

  public translateInvestigationAssignedUserType(assignedUserType: AssignedUserType): string {
    if (typeof this.translations.investigationAssignedUserTypes === 'undefined') {
      this.translations.investigationAssignedUserTypes = {
        [AssignedUserType.UNASSIGNED]: translateFn('Unassigned'),
        [AssignedUserType.USER_OUTSIDE_MY_COMPANY]: translateFn('User outside my organization'),
      };
    }

    return this.translations.investigationAssignedUserTypes[assignedUserType] as string;
  }

  public translateInvestigationPriority(investigationPriority: InvestigationPriority): string {
    if (typeof this.translations.investigationPriorities === 'undefined') {
      this.translations.investigationPriorities = {
        [InvestigationPriority.UNKNOWN]: translateFn('Unassigned'),
        [InvestigationPriority.LOW]: translateFn('Low'),
        [InvestigationPriority.MEDIUM]: translateFn('Medium'),
        [InvestigationPriority.HIGH]: translateFn('High'),
        [InvestigationPriority.CRITICAL]: translateFn('Critical'),
      };
    }

    return this.translations.investigationPriorities[investigationPriority];
  }
}
